import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  getAdminStats,
  getAdminUsers,
  getAdminUser,
  updateAdminUser,
  deleteAdminUser,
  sendPasswordReset,
  getAdminPricing,
  updateAdminPricing,
  getPricingEnforcement,
  updatePricingEnforcement,
  getAIAnalysisSettings,
  updateAIAnalysisSettings,
  testAIAnalysisSettings,
  getAITradingAgentSettings,
  updateAITradingAgentSettings,
  testAITradingAgentSettings,
  getAdminNewsEvents,
  updateAdminNewsEvents,
  getAdminSupportTickets,
  updateSupportTicket,
  getAdminExecutionOverview,
  getAdminExecutionJobs,
  getAdminFillPollHistory,
  getAdminFillPollStats,
  retryExecutionJob,
  previewDeadLetterCleanup,
  applyDeadLetterCleanup,
  getAdminBrokers,
  updateAdminBroker,
  getAdminMarketDataRefreshJobs,
  getAdminMarketDataCoverage,
  getAdminMarketDataSymbols,
  triggerMarketDataRefresh,
  previewBrokerGapRepair,
  applyBrokerGapRepair,
  getMarketDataBrowser,
  getAdminMarketSessionConfig,
  updateAdminMarketSessionConfig,
  getSystemSettings,
  updateSystemSettings,
  getNotificationSummarySchedule,
  updateNotificationSummarySchedule,
  getKillSwitchStatus,
  toggleKillSwitch,
  getResendStatus,
} from '../services/api.admin';
import {
  getStrategyCatalogAdmin,
  createCatalogStrategy,
  deleteCatalogStrategy,
  updateCatalogStrategy,
} from '../services/api.strategies';
import {
  Activity, Users, CreditCard, TrendingUp, Search, Mail,
  Shield, Trash2, Edit, Eye, RefreshCw, Settings, MessageSquare,
  DollarSign, Calendar, ChevronDown, Check, X, AlertCircle,
  FolderOpen, Folder, FileText, AlertTriangle,
  Octagon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import PlatformShell from '../components/PlatformShell';
import StrategyRuntimeFields from '../components/StrategyRuntimeFields';
import UsersTab from '../components/admin/UsersTab';
import PricingTab from '../components/admin/PricingTab';
import AITab from '../components/admin/AITab';
import BrokersTab from '../components/admin/BrokersTab';
import OperationsTab from '../components/admin/OperationsTab';
import StrategiesTab from '../components/admin/StrategiesTab';
import SupportTab from '../components/admin/SupportTab';
import WatchdogTab from '../components/admin/WatchdogTab';
import AutomationTimingTab from '../components/admin/AutomationTimingTab';
import { getApiErrorMessage } from '../lib/apiErrors';
import {
  dateTimeLocalInputElementToIso,
  formatDateDisplay,
  formatDateTimeDisplay,
  resolveDisplayTimezone,
  toDateTimeLocalInputValue,
} from '../lib/datetime';
import {
  STRATEGY_ENGINES,
  buildRuntimeDefinition,
  getDefaultRuntimeParams,
} from '../lib/strategyRuntime';
import { buildInstrumentOption, getKnownInstrumentCatalog } from '../lib/instruments';
import useMediaQuery from '../hooks/useMediaQuery';

const DEFAULT_MARKET_DATA_TIMEFRAMES = ['2m', '3m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1mo'];
const DEAD_LETTER_CLEANUP_CONFIRMATION = 'DELETE_OLD_INACTIVE_DEAD_LETTERS';
const DEFAULT_AI_ANALYSIS_SETTINGS = {
  enabled: false,
  provider_label: 'Local Ollama',
  mode: 'ollama',
  model: 'ollama/gemma4:e4b',
  api_base: 'http://host.docker.internal:11434',
  api_key_env: '',
  api_key: '',
  api_key_set: false,
  api_key_source: 'none',
  clear_api_key: false,
  temperature: 0.2,
  max_tokens: 400,
  timeout_seconds: 20,
  system_prompt: "You are TradoPro's market analysis assistant. Use the provided technical context only. Be concise, practical, and cautious. Follow the admin's requested output format directly.",
  fallback_to_rules: true,
  use_for_market_summary: false,
  updated_at: null,
};
const DEFAULT_AI_TRADING_AGENT_SETTINGS = {
  enabled: false,
  provider_label: 'Anthropic',
  mode: 'custom',
  model: 'anthropic/claude-fable-5',
  api_base: '',
  api_key_env: 'ANTHROPIC_API_KEY',
  api_key: '',
  api_key_set: false,
  api_key_source: 'none',
  clear_api_key: false,
  temperature: 0,
  max_tokens: 600,
  timeout_seconds: 30,
  system_prompt: "You are TradoPro's independent futures entry-review agent. Evaluate one proposed trade at a time using only the supplied broker-grounded market, account, and risk snapshot. Prefer NO_TRADE when evidence is incomplete.",
  updated_at: null,
};
const AI_TRADING_AGENT_PRESETS = {
  anthropic_fable: {
    provider_label: 'Anthropic Claude Fable 5', mode: 'custom', model: 'anthropic/claude-fable-5', api_base: '', api_key_env: 'ANTHROPIC_API_KEY',
  },
  anthropic_sonnet: {
    provider_label: 'Anthropic Claude Sonnet 5', mode: 'custom', model: 'anthropic/claude-sonnet-5', api_base: '', api_key_env: 'ANTHROPIC_API_KEY',
  },
  openai_frontier: {
    provider_label: 'OpenAI GPT-5.5', mode: 'openai_compatible', model: 'gpt-5.5', api_base: '', api_key_env: 'OPENAI_API_KEY',
  },
  openai_latest: {
    provider_label: 'OpenAI GPT-5.6', mode: 'openai_compatible', model: 'gpt-5.6', api_base: '', api_key_env: 'OPENAI_API_KEY',
  },
  custom: {
    provider_label: 'Custom Trading Agent', mode: 'custom', model: '', api_base: '', api_key_env: '',
  },
};
const detectAiTradingAgentPreset = (settings) => {
  const model = String(settings?.model || '').toLowerCase();
  if (model.includes('claude-fable-5')) return 'anthropic_fable';
  if (model.includes('claude-sonnet-5')) return 'anthropic_sonnet';
  if (model === 'gpt-5.6') return 'openai_latest';
  if (model === 'gpt-5.5') return 'openai_frontier';
  return 'custom';
};
const parseAiNumberValue = (value) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};
const AI_PROVIDER_PRESETS = {
  ollama: {
    label: 'Ollama (local/free)',
    mode: 'ollama',
    enabled: true,
    provider_label: 'Local Ollama',
    model: 'ollama/gemma4:e4b',
    api_base: 'http://host.docker.internal:11434',
    api_key_env: '',
  },
  lm_studio: {
    label: 'LM Studio (local)',
    mode: 'openai_compatible',
    enabled: true,
    provider_label: 'LM Studio',
    model: 'openai/local-model',
    api_base: 'http://127.0.0.1:1234/v1',
    api_key_env: '',
  },
  openai_chatgpt: {
    label: 'ChatGPT / OpenAI',
    mode: 'openai_compatible',
    enabled: true,
    provider_label: 'ChatGPT / OpenAI',
    model: 'gpt-5-mini',
    api_base: '',
    api_key_env: 'OPENAI_API_KEY',
    temperature: 1,
  },
  gemini: {
    label: 'Gemini',
    mode: 'custom',
    enabled: true,
    provider_label: 'Gemini',
    model: 'gemini/gemini-2.5-flash',
    api_base: '',
    api_key_env: 'GEMINI_API_KEY',
  },
  anthropic: {
    label: 'Anthropic',
    mode: 'custom',
    enabled: true,
    provider_label: 'Anthropic',
    model: 'anthropic/claude-sonnet-4-5',
    api_base: '',
    api_key_env: 'ANTHROPIC_API_KEY',
  },
  openai_compatible: {
    label: 'OpenAI-compatible (generic)',
    mode: 'openai_compatible',
    enabled: true,
    provider_label: 'OpenAI-Compatible',
    model: 'openai/local-model',
    api_base: '',
    api_key_env: '',
  },
  custom: {
    label: 'Custom LiteLLM model',
    mode: 'custom',
    enabled: true,
    provider_label: 'Custom LiteLLM',
    model: '',
    api_base: '',
    api_key_env: '',
  },
  disabled: {
    label: 'Disabled / rules only',
    mode: 'disabled',
    enabled: false,
    provider_label: 'Disabled',
    model: '',
    api_base: '',
    api_key_env: '',
  },
};

const detectAiProviderPreset = (settings) => {
  const mode = String(settings?.mode || '').toLowerCase();
  const label = String(settings?.provider_label || '').toLowerCase();
  const model = String(settings?.model || '').toLowerCase();
  const apiBase = String(settings?.api_base || '').toLowerCase();
  const apiKeyEnv = String(settings?.api_key_env || '').toUpperCase();

  if (mode === 'disabled' || label === 'disabled') {
    return 'disabled';
  }
  if (label.includes('lm studio') || apiBase.includes('127.0.0.1:1234')) {
    return 'lm_studio';
  }
  if (model.startsWith('ollama/') || label.includes('ollama') || apiBase.includes(':11434')) {
    return 'ollama';
  }
  if (model.startsWith('gemini/') || apiKeyEnv === 'GEMINI_API_KEY' || label.includes('gemini')) {
    return 'gemini';
  }
  if (model.startsWith('anthropic/') || apiKeyEnv === 'ANTHROPIC_API_KEY' || label.includes('anthropic')) {
    return 'anthropic';
  }
  if (mode === 'openai_compatible' && (label.includes('compatible') || model.startsWith('openai/'))) {
    return 'openai_compatible';
  }
  if (model.startsWith('gpt-') || apiKeyEnv === 'OPENAI_API_KEY' || label.includes('chatgpt')) {
    return 'openai_chatgpt';
  }
  if (mode === 'openai_compatible') {
    return 'openai_compatible';
  }
  if (mode === 'custom') {
    return 'custom';
  }
  return 'custom';
};

const looksLikeDirectApiKey = (value) => {
  const candidate = String(value || '').trim();
  if (!candidate) {
    return false;
  }
  if (/^(sk-|AIza|gsk_|rk-|xai-)/.test(candidate)) {
    return true;
  }
  return candidate.length >= 24 && !/^[A-Z0-9_]+$/.test(candidate);
};

const createEmptyNewsEventDraft = () => ({
  id: `evt-${Math.random().toString(36).slice(2, 10)}`,
  title: '',
  starts_at: '',
  impact: 'medium',
  affected_symbols: ['ES'],
  market_impact: '',
  source: '',
  source_url: '',
  notes: '',
  is_active: true,
});

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const appTimeZone = resolveDisplayTimezone(user?.timezone);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pricing, setPricing] = useState({});
  const [pricingSettings, setPricingSettings] = useState({ payments_enforced: true });
  const [aiAnalysisSettings, setAiAnalysisSettings] = useState(DEFAULT_AI_ANALYSIS_SETTINGS);
  const [aiTradingAgentSettings, setAiTradingAgentSettings] = useState(DEFAULT_AI_TRADING_AGENT_SETTINGS);
  const [adminLoadWarnings, setAdminLoadWarnings] = useState([]);
  const [marketNewsEvents, setMarketNewsEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [executionOverview, setExecutionOverview] = useState({ jobs: {}, live_trades: {}, dead_letters: [], fill_polling: {} });
  const [executionJobs, setExecutionJobs] = useState([]);
  const [fillPollHistory, setFillPollHistory] = useState([]);
  const [fillPollStats, setFillPollStats] = useState({ logs: [] });
  const [marketDataJobs, setMarketDataJobs] = useState([]);
  const [marketDataCoverage, setMarketDataCoverage] = useState([]);
  const [marketDataSymbols, setMarketDataSymbols] = useState([]);
  const [marketSessionConfigDraft, setMarketSessionConfigDraft] = useState('');
  const [systemSettings, setSystemSettings] = useState({ 
    data_refresh_rate_ms: 5000, 
    payments_enforced: false,
    kill_switch_enabled: false,
    live_order_require_fresh_quote: true,
    live_order_quote_max_age_seconds: 12,
    signal_recipient_active_days: 30,
  });
  const [resendStatus, setResendStatus] = useState(null);
  const [notificationSummaryTimes, setNotificationSummaryTimes] = useState(['08:30', '09:30', '10:00', '12:00', '15:30']);
  const [newNotificationSummaryTime, setNewNotificationSummaryTime] = useState('');
  const [savingNotificationSummaryTimes, setSavingNotificationSummaryTimes] = useState(false);
  const [showKillSwitchConfirm, setShowKillSwitchConfirm] = useState(false);
  const [killSwitchProcessing, setKillSwitchProcessing] = useState(false);
  
  const [savingSystemSettings, setSavingSystemSettings] = useState(false);
  const [runningMarketRefresh, setRunningMarketRefresh] = useState(false);
  const [marketRefreshForm, setMarketRefreshForm] = useState({
    provider: 'dukascopy',
    symbol: 'BTCUSD',
    csv_path: '',
    source_timezone: 'UTC',
    session_type: 'no_session',
    mode: 'incremental',
    timeframes: DEFAULT_MARKET_DATA_TIMEFRAMES.join(','),
  });
  const [brokerGapRepairForm, setBrokerGapRepairForm] = useState({
    symbol: 'MNQ',
    start_time: '',
    end_time: '',
    maximum_bars: 2000,
  });
  const [brokerGapRepairPreview, setBrokerGapRepairPreview] = useState(null);
  const [brokerGapRepairConfirmation, setBrokerGapRepairConfirmation] = useState('');
  const [previewingBrokerGapRepair, setPreviewingBrokerGapRepair] = useState(false);
  const [applyingBrokerGapRepair, setApplyingBrokerGapRepair] = useState(false);
  const brokerGapRepairStartInputRef = useRef(null);
  const brokerGapRepairEndInputRef = useRef(null);
  const [catalogStrategies, setCatalogStrategies] = useState([]);
  const [adminBrokers, setAdminBrokers] = useState([]);
  const [brokerInstrumentDrafts, setBrokerInstrumentDrafts] = useState({});
  const [brokerInstrumentInputs, setBrokerInstrumentInputs] = useState({});
  const [newStrategy, setNewStrategy] = useState({
    name: '',
    description: '',
    min_plan: 'FREE',
    code: '',
    runtime_engine: 'TREND_FOLLOWING',
    runtime_params: getDefaultRuntimeParams('TREND_FOLLOWING'),
  });
  const [showStrategyDialog, setShowStrategyDialog] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [mobileAdminSection, setMobileAdminSection] = useState('overview');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  
  // Dialogs
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editedPricing, setEditedPricing] = useState({});
  const [retryingExecutionJobId, setRetryingExecutionJobId] = useState(null);
  const [deadLetterCleanupPreview, setDeadLetterCleanupPreview] = useState(null);
  const [deadLetterCleanupOlderThanHours, setDeadLetterCleanupOlderThanHours] = useState(24);
  const [deadLetterCleanupIncludeClose, setDeadLetterCleanupIncludeClose] = useState(false);
  const [deadLetterCleanupConfirmation, setDeadLetterCleanupConfirmation] = useState('');
  const [deadLetterCleanupPreviewing, setDeadLetterCleanupPreviewing] = useState(false);
  const [deadLetterCleanupApplying, setDeadLetterCleanupApplying] = useState(false);
  const [savingPricingSettings, setSavingPricingSettings] = useState(false);
  const [savingAiSettings, setSavingAiSettings] = useState(false);
  const [savingMarketNewsEvents, setSavingMarketNewsEvents] = useState(false);
  const [testingAiSettings, setTestingAiSettings] = useState(false);
  const [aiSettingsTestResult, setAiSettingsTestResult] = useState(null);
  const [savingAiTradingAgentSettings, setSavingAiTradingAgentSettings] = useState(false);
  const [testingAiTradingAgentSettings, setTestingAiTradingAgentSettings] = useState(false);
  const [aiTradingAgentTestResult, setAiTradingAgentTestResult] = useState(null);
  const [savingBrokerId, setSavingBrokerId] = useState(null);
  const [savingMarketSessionConfig, setSavingMarketSessionConfig] = useState(false);
  const [showMarketBrowserDialog, setShowMarketBrowserDialog] = useState(false);
  const [marketBrowserLoading, setMarketBrowserLoading] = useState(false);
  const [marketBrowserRoots, setMarketBrowserRoots] = useState([]);
  const [marketBrowserEntries, setMarketBrowserEntries] = useState([]);
  const [marketBrowserCurrentPath, setMarketBrowserCurrentPath] = useState('');
  const [marketBrowserParentPath, setMarketBrowserParentPath] = useState(null);
  const knownInstrumentCatalog = useMemo(() => getKnownInstrumentCatalog(), []);
  const marketDataSymbolOptions = useMemo(() => {
    const fallbackValues = knownInstrumentCatalog.map((instrument) => instrument.value);
    const symbols = marketDataSymbols.length ? marketDataSymbols : fallbackValues;
    return symbols
      .map((symbol) => buildInstrumentOption(symbol))
      .filter(Boolean);
  }, [knownInstrumentCatalog, marketDataSymbols]);
  const aiProviderPresetKey = useMemo(
    () => detectAiProviderPreset(aiAnalysisSettings),
    [aiAnalysisSettings],
  );
  const selectedAiProviderPreset = AI_PROVIDER_PRESETS[aiProviderPresetKey] || AI_PROVIDER_PRESETS.custom;
  const apiKeyEnvLooksLikeSecret = useMemo(
    () => looksLikeDirectApiKey(aiAnalysisSettings.api_key_env),
    [aiAnalysisSettings.api_key_env],
  );
  const aiTradingAgentPresetKey = useMemo(
    () => detectAiTradingAgentPreset(aiTradingAgentSettings),
    [aiTradingAgentSettings],
  );
  const selectedAiTradingAgentPreset = AI_TRADING_AGENT_PRESETS[aiTradingAgentPresetKey] || AI_TRADING_AGENT_PRESETS.custom;
  const agentApiKeyEnvLooksLikeSecret = useMemo(
    () => looksLikeDirectApiKey(aiTradingAgentSettings.api_key_env),
    [aiTradingAgentSettings.api_key_env],
  );

  const applyAiSettingsState = useCallback(
    (data) => {
      setAiAnalysisSettings({
        ...DEFAULT_AI_ANALYSIS_SETTINGS,
        ...(data || {}),
        api_key: '',
        clear_api_key: false,
      });
    },
    [setAiAnalysisSettings],
  );

  const reloadAiSettings = useCallback(async () => {
    try {
      const { data } = await getAIAnalysisSettings();
      applyAiSettingsState(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to refresh AI analysis settings'));
    }
  }, [applyAiSettingsState]);

  const reloadAiTradingAgentSettings = useCallback(async () => {
    try {
      const { data } = await getAITradingAgentSettings();
      setAiTradingAgentSettings({
        ...DEFAULT_AI_TRADING_AGENT_SETTINGS,
        ...(data || {}),
        api_key: '',
        clear_api_key: false,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to refresh AI trading agent settings'));
    }
  }, []);

  const updateCatalogRuntimeEngine = (engine) => {
    setNewStrategy((current) => ({
      ...current,
      runtime_engine: engine,
      runtime_params: getDefaultRuntimeParams(engine),
    }));
  };

  const updateCatalogRuntimeParam = (key, value) => {
    setNewStrategy((current) => ({
      ...current,
      runtime_params: {
        ...current.runtime_params,
        [key]: value,
      },
    }));
  };

  const updateEditingRuntimeEngine = (engine) => {
    setEditingStrategy((current) => current ? ({
      ...current,
      runtime_definition: buildRuntimeDefinition(engine, getDefaultRuntimeParams(engine)),
    }) : current);
  };

  const updateEditingRuntimeParam = (key, value) => {
    setEditingStrategy((current) => current ? ({
      ...current,
      runtime_definition: {
        ...(current.runtime_definition || buildRuntimeDefinition('TREND_FOLLOWING')),
        params: {
          ...getDefaultRuntimeParams(current.runtime_definition?.engine || 'TREND_FOLLOWING'),
          ...(current.runtime_definition?.params || {}),
          [key]: value,
        },
      },
    }) : current);
  };

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('Access denied. Admin privileges required.');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchCatalogStrategies = useCallback(async () => {
    try {
      const res = await getStrategyCatalogAdmin();
      setCatalogStrategies(res.data?.strategies || []);
    } catch (error) {
      console.error('Error fetching strategy catalog:', error);
    }
  }, []);

  const handleCreateCatalogStrategy = async () => {
    if (!newStrategy.name?.trim() || !newStrategy.code?.trim()) {
      toast.error('Name and Python code are required');
      return;
    }

    try {
      await createCatalogStrategy({
        name: newStrategy.name.trim(),
        description: newStrategy.description?.trim() || null,
        min_plan: newStrategy.min_plan,
        language: 'python',
        code: newStrategy.code,
        runtime: buildRuntimeDefinition(newStrategy.runtime_engine, newStrategy.runtime_params),
      });
      toast.success('Strategy uploaded');
      setNewStrategy({
        name: '',
        description: '',
        min_plan: 'FREE',
        code: '',
        runtime_engine: 'TREND_FOLLOWING',
        runtime_params: getDefaultRuntimeParams('TREND_FOLLOWING'),
      });
      fetchCatalogStrategies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload strategy'));
    }
  };

  const handleDeleteCatalogStrategy = async (strategyId) => {
    try {
      await deleteCatalogStrategy(strategyId);
      toast.success('Strategy removed');
      fetchCatalogStrategies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete strategy'));
    }
  };

  const handleToggleCatalogStrategy = async (strategy) => {
    try {
      await updateCatalogStrategy(strategy.id, {
        is_active: !strategy.is_active
      });
      toast.success(`Strategy ${strategy.is_active ? 'disabled' : 'enabled'}`);
      fetchCatalogStrategies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update strategy'));
    }
  };

  const openEditStrategy = (strategy) => {
    setEditingStrategy({
      ...strategy,
      code: strategy.code || '',
      runtime_definition: strategy.runtime_definition || buildRuntimeDefinition('TREND_FOLLOWING'),
    });
    setShowStrategyDialog(true);
  };

  const handleSaveStrategy = async () => {
    if (!editingStrategy?.id) return;
    setSavingStrategy(true);
    try {
      await updateCatalogStrategy(editingStrategy.id, {
        name: editingStrategy.name,
        description: editingStrategy.description,
        code: editingStrategy.code,
        min_plan: editingStrategy.min_plan,
        runtime: editingStrategy.runtime_definition,
        is_active: editingStrategy.is_active,
      });
      toast.success('Strategy updated');
      setShowStrategyDialog(false);
      setEditingStrategy(null);
      fetchCatalogStrategies();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save strategy'));
    } finally {
      setSavingStrategy(false);
    }
  };

  const requestToggleCatalogStrategy = (strategy) => {
    const action = strategy.is_active ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} this strategy?`)) return;
    handleToggleCatalogStrategy(strategy);
  };

  const requestDeleteCatalogStrategy = (strategyId) => {
    if (!window.confirm('Delete this strategy from catalog? This cannot be undone.')) return;
    handleDeleteCatalogStrategy(strategyId);
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const warnings = [];
      const ensureObject = (value, fallback = {}) => (
        value && typeof value === 'object' && !Array.isArray(value) ? value : fallback
      );
      const ensureArray = (value, fallback = []) => (
        Array.isArray(value) ? value : fallback
      );
      const ensureJsonArray = (value, fallback = []) => {
        if (Array.isArray(value)) {
          return value;
        }
        if (typeof value === 'string' && value.trim()) {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : fallback;
          } catch (error) {
            return fallback;
          }
        }
        return fallback;
      };
      const runSection = (label, callback) => {
        try {
          callback();
        } catch (error) {
          console.error(`Error normalizing admin ${label}:`, error);
          if (!warnings.includes(label)) {
            warnings.push(label);
          }
        }
      };
      const withFallback = async (label, request, fallback) => {
        try {
          return await request;
        } catch (error) {
          console.error(`Error fetching admin ${label}:`, error);
          warnings.push(label);
          return { data: fallback };
        }
      };
      const [statsRes, usersRes, pricingRes, pricingSettingsRes, newsEventsRes, ticketsRes, executionRes, executionJobsRes, fillPollHistoryRes, fillPollStatsRes, brokersRes, marketDataJobsRes, marketDataCoverageRes, marketDataSymbolsRes, marketSessionConfigRes, systemSettingsRes, notificationSummaryScheduleRes, resendStatusRes, killSwitchRes] = await Promise.all([
        withFallback('stats', getAdminStats(), {
          total_users: 0,
          new_users_this_month: 0,
          active_subscriptions: 0,
          revenue_monthly: 0,
          revenue_total: 0,
          users_by_plan: {},
        }),
        withFallback(
          'users',
          getAdminUsers({ skip: currentPage * pageSize, limit: pageSize, search: searchTerm, plan_filter: planFilter }),
          { users: [], total: 0 }
        ),
        withFallback('pricing', getAdminPricing(), {}),
        getPricingEnforcement().catch(() => ({ data: { payments_enforced: true } })),
        getAdminNewsEvents().catch(() => ({ data: { events: [] } })),
        withFallback('support tickets', getAdminSupportTickets(), { tickets: [] }),
        getAdminExecutionOverview().catch(() => ({ data: { jobs: {}, live_trades: {}, dead_letters: [], fill_polling: {} } })),
        getAdminExecutionJobs({ limit: 15 }).catch(() => ({ data: { jobs: [] } })),
        getAdminFillPollHistory({ limit: 20 }).catch(() => ({ data: { logs: [] } })),
        getAdminFillPollStats({ days: 7 }).catch(() => ({ data: { logs: [] } })),
        getAdminBrokers().catch(() => ({ data: { brokers: [] } })),
        getAdminMarketDataRefreshJobs({ limit: 20 }).catch(() => ({ data: { jobs: [] } })),
        getAdminMarketDataCoverage().catch(() => ({ data: { coverage: [] } })),
        getAdminMarketDataSymbols().catch(() => ({
          data: { symbols: knownInstrumentCatalog.map((instrument) => instrument.value) }
        })),
        getAdminMarketSessionConfig().catch(() => ({ data: { config: null } })),
        getSystemSettings().catch(() => ({
          data: {
            data_refresh_rate_ms: 5000,
            payments_enforced: false,
            live_order_require_fresh_quote: true,
            live_order_quote_max_age_seconds: 12,
            signal_recipient_active_days: 30,
          },
        })),
        getNotificationSummarySchedule().catch(() => ({
          data: { times: ['08:30', '09:30', '10:00', '12:00', '15:30'] },
        })),
        getResendStatus().catch(() => ({ data: null })),
        getKillSwitchStatus().catch(() => ({ data: { enabled: false } })),
      ]);
      setAdminLoadWarnings(warnings);

      const statsData = ensureObject(statsRes?.data, {
        total_users: 0,
        new_users_this_month: 0,
        active_subscriptions: 0,
        revenue_monthly: 0,
        revenue_total: 0,
        users_by_plan: {},
      });
      const usersData = ensureObject(usersRes?.data, { users: [], total: 0 });
      const pricingData = ensureObject(pricingRes?.data, {});
      const pricingSettingsData = ensureObject(pricingSettingsRes?.data, { payments_enforced: true });
      const newsEventsData = ensureObject(newsEventsRes?.data, { events: [] });
      const ticketsData = ensureObject(ticketsRes?.data, { tickets: [] });
      const executionOverviewData = ensureObject(executionRes?.data, { jobs: {}, live_trades: {}, dead_letters: [], fill_polling: {} });
      const executionJobsData = ensureObject(executionJobsRes?.data, { jobs: [] });
      const fillPollHistoryData = ensureObject(fillPollHistoryRes?.data, { logs: [] });
      const fillPollStatsData = ensureObject(fillPollStatsRes?.data, { logs: [] });
      const brokersData = ensureObject(brokersRes?.data, { brokers: [] });
      const marketDataJobsData = ensureObject(marketDataJobsRes?.data, { jobs: [] });
      const marketDataCoverageData = ensureObject(marketDataCoverageRes?.data, { coverage: [] });
      const marketDataSymbolsData = ensureObject(marketDataSymbolsRes?.data, { symbols: [] });
      const marketSessionConfigData = ensureObject(marketSessionConfigRes?.data, { config: null });
      const sysSettings = ensureObject(systemSettingsRes?.data, {
        data_refresh_rate_ms: 5000,
        payments_enforced: false,
        live_order_require_fresh_quote: true,
        live_order_quote_max_age_seconds: 12,
        signal_recipient_active_days: 30,
      });
      const killSwitchData = ensureObject(killSwitchRes?.data, { enabled: false });

      runSection('stats', () => {
        setStats(statsData);
      });
      runSection('users', () => {
        setUsers(ensureArray(usersData.users));
        setTotalUsers(Number(usersData.total) || 0);
      });
      runSection('pricing', () => {
        setPricing(pricingData);
        setPricingSettings(pricingSettingsData);
      });
      runSection('system', () => {
        setSystemSettings({
          ...sysSettings,
          kill_switch_enabled: Boolean(killSwitchData.enabled),
        });
        setNotificationSummaryTimes(ensureArray(notificationSummaryScheduleRes?.data?.times));
        setResendStatus(resendStatusRes?.data ?? null);
      });
      runSection('news events', () => {
        setMarketNewsEvents(ensureArray(newsEventsData.events));
      });
      runSection('support tickets', () => {
        setTickets(ensureArray(ticketsData.tickets));
      });
      runSection('execution', () => {
        setExecutionOverview(executionOverviewData);
        setExecutionJobs(ensureArray(executionJobsData.jobs));
        setFillPollHistory(ensureArray(fillPollHistoryData.logs));
        setFillPollStats(fillPollStatsData);
      });
      runSection('market data', () => {
        setMarketDataJobs(ensureArray(marketDataJobsData.jobs));
        setMarketDataCoverage(ensureArray(marketDataCoverageData.coverage));
        setMarketDataSymbols(ensureArray(marketDataSymbolsData.symbols));
        setMarketSessionConfigDraft(
          marketSessionConfigData.config && typeof marketSessionConfigData.config === 'object'
            ? JSON.stringify(marketSessionConfigData.config, null, 2)
            : ''
        );
      });
      runSection('brokers', () => {
        const brokers = ensureArray(brokersData.brokers)
          .filter((broker) => broker && typeof broker === 'object')
          .map((broker) => ({
            ...broker,
            instruments: ensureArray(broker.instruments),
            supported_instruments: ensureJsonArray(broker.supported_instruments),
            trading_hours: {
              start: broker?.trading_hours?.start || '',
              end: broker?.trading_hours?.end || '',
              timezone: broker?.trading_hours?.timezone || 'America/New_York',
            },
            enforce_trading_hours: broker?.enforce_trading_hours ?? true,
            close_at_end_of_session: broker?.close_at_end_of_session ?? true,
          }));
        setAdminBrokers(brokers);
        setBrokerInstrumentDrafts(Object.fromEntries(
          brokers.map((broker) => [
            broker.id,
            Array.isArray(broker.instruments) && broker.instruments.length
              ? broker.instruments
              : ensureJsonArray(broker.supported_instruments).map((symbol, index) => {
                  const option = buildInstrumentOption(symbol);
                  return {
                    symbol,
                    display_label: option?.label || symbol,
                    display_name: option?.name || symbol,
                    instrument_type: ['BTCUSD', 'ETHUSD'].includes(symbol)
                      ? 'crypto'
                      : symbol === 'EURUSD'
                        ? 'forex'
                        : 'futures',
                    symbol_id: '',
                    contract_id: '',
                    aliases: [],
                    sort_order: index,
                    is_active: true,
                  };
                }),
          ])
        ));
      });
      setAiSettingsTestResult(null);
      setAdminLoadWarnings(warnings);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setAdminLoadWarnings(['dashboard']);
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
      await Promise.all([reloadAiSettings(), reloadAiTradingAgentSettings()]);
    }
  }, [currentPage, searchTerm, planFilter, navigate, reloadAiSettings, reloadAiTradingAgentSettings]);

  useEffect(() => {
    fetchData();
    fetchCatalogStrategies();
  }, [fetchData, fetchCatalogStrategies]);

  // User management functions
  const handleViewUser = async (userId) => {
    try {
      const res = await getAdminUser(userId);
      setSelectedUser(res.data);
      setShowUserDialog(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await updateAdminUser(userId, updates);
      toast.success('User updated successfully');
      fetchData();
      setShowUserDialog(false);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleSendPasswordReset = async (userId) => {
    try {
      await sendPasswordReset(userId);
      toast.success('Password reset email sent');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send reset email'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    
    try {
      await deleteAdminUser(userId);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete user'));
    }
  };

  // Pricing management
  const handleEditPricing = (plan) => {
    setSelectedPlan(plan);
    setEditedPricing(JSON.parse(JSON.stringify(pricing[plan] || {})));
    setShowPricingDialog(true);
  };

  const handleSavePricing = async () => {
    try {
      await updateAdminPricing(selectedPlan, {
        plan: selectedPlan,
        ...editedPricing
      });
      toast.success('Pricing updated');
      fetchData();
      setShowPricingDialog(false);
    } catch (error) {
      toast.error('Failed to update pricing');
    }
  };

  const handleTogglePricingEnforcement = async (checked) => {
    setSavingPricingSettings(true);
    try {
      const { data } = await updatePricingEnforcement({
        payments_enforced: checked,
      });
      const newVal = { payments_enforced: data.payments_enforced };
      setPricingSettings(newVal);
      setSystemSettings((current) => ({ ...current, ...newVal }));
      toast.success(
        checked
          ? 'Stripe checkout is now enforced'
          : 'Pricing enforcement disabled — plan upgrades will bypass payment'
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update pricing settings'));
    } finally {
      setSavingPricingSettings(false);
    }
  };

  const handleToggleKillSwitch = async () => {
    const newState = !systemSettings.kill_switch_enabled;
    setKillSwitchProcessing(true);
    try {
      const { data } = await toggleKillSwitch(newState);
      setSystemSettings(prev => ({ ...prev, kill_switch_enabled: data.enabled }));
      toast.success(
        data.enabled 
          ? 'KILL SWITCH ACTIVATED: All orders cancelled.' 
          : 'System returned to normal operation.'
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to toggle kill switch'));
    } finally {
      setKillSwitchProcessing(false);
      setShowKillSwitchConfirm(false);
    }
  };

  const togglePricingEnforcement = () => {
    if (savingPricingSettings) return;
    handleTogglePricingEnforcement(!Boolean(pricingSettings.payments_enforced));
  };

  const applyAiProviderPreset = (presetKey) => {
    const preset = AI_PROVIDER_PRESETS[presetKey];
    if (!preset) {
      return;
    }
    setAiAnalysisSettings((current) => {
      if (presetKey === 'disabled') {
        return {
          ...current,
          enabled: false,
          mode: 'disabled',
          provider_label: 'Disabled',
        };
      }
      return {
        ...current,
        enabled: preset.enabled,
        mode: preset.mode,
        provider_label: preset.provider_label,
        model: preset.model,
        api_base: preset.api_base,
        api_key_env: preset.api_key_env,
        temperature: preset.temperature ?? current.temperature,
      };
    });
    setAiSettingsTestResult(null);
  };

  const numericAiFields = new Set(['temperature', 'max_tokens', 'timeout_seconds']);
  const handleAiSettingsField = (field, value) => {
    setAiAnalysisSettings((current) => {
      if (field === 'enabled') {
        if (value) {
          const nextMode = current.mode === 'disabled' ? 'ollama' : current.mode;
          const nextProviderLabel = current.mode === 'disabled' && (!current.provider_label || current.provider_label === 'Disabled')
            ? 'Local Ollama'
            : current.provider_label;
          return {
            ...current,
            enabled: true,
            mode: nextMode,
            provider_label: nextProviderLabel,
            model: nextMode === 'ollama' && !current.model ? 'ollama/gemma4:e4b' : current.model,
            api_base: nextMode === 'ollama' && !current.api_base ? 'http://host.docker.internal:11434' : current.api_base,
          };
        }
        return {
          ...current,
          enabled: false,
        };
      }
      const isNumericField = numericAiFields.has(field);
      const normalizedValue = isNumericField
        ? (value === '' || value === null || value === undefined ? '' : Number(value))
        : value;
      return {
        ...current,
        [field]: normalizedValue,
      };
    });
    setAiSettingsTestResult(null);
  };

  const buildAiSettingsPayload = useCallback(() => {
    const temperature = parseAiNumberValue(aiAnalysisSettings.temperature);
    const maxTokens = parseAiNumberValue(aiAnalysisSettings.max_tokens);
    const timeoutSeconds = parseAiNumberValue(aiAnalysisSettings.timeout_seconds);
    const payload = {
      enabled: Boolean(aiAnalysisSettings.enabled),
      provider_label: aiAnalysisSettings.provider_label,
      mode: aiAnalysisSettings.mode,
      model: aiAnalysisSettings.model,
      api_base: aiAnalysisSettings.api_base,
      api_key_env: aiAnalysisSettings.api_key_env,
      clear_api_key: Boolean(aiAnalysisSettings.clear_api_key),
      system_prompt: aiAnalysisSettings.system_prompt,
      fallback_to_rules: Boolean(aiAnalysisSettings.fallback_to_rules),
      use_for_market_summary: Boolean(aiAnalysisSettings.use_for_market_summary),
    };
    if (aiAnalysisSettings.api_key?.trim()) {
      payload.api_key = aiAnalysisSettings.api_key.trim();
    }
    if (temperature !== undefined) {
      payload.temperature = temperature;
    }
    if (maxTokens !== undefined) {
      payload.max_tokens = maxTokens;
    }
    if (timeoutSeconds !== undefined) {
      payload.timeout_seconds = timeoutSeconds;
    }
    return payload;
  }, [aiAnalysisSettings]);

  const handleSaveAiSettings = async () => {
    setSavingAiSettings(true);
    try {
      const payload = buildAiSettingsPayload();
      await updateAIAnalysisSettings(payload);
      await reloadAiSettings();
      toast.success('AI analysis settings updated');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update AI analysis settings'));
    } finally {
      setSavingAiSettings(false);
    }
  };

  const handleTestAiSettings = async () => {
    setTestingAiSettings(true);
    setAiSettingsTestResult(null);
    try {
      const payload = buildAiSettingsPayload();
      const { data } = await testAIAnalysisSettings(payload);
      setAiSettingsTestResult(data || { ok: true, message: 'Connection successful' });
      toast.success(data?.message || 'AI provider test succeeded');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to test AI provider');
      setAiSettingsTestResult({ ok: false, message });
      toast.error(message);
    } finally {
      setTestingAiSettings(false);
    }
  };

  const applyAiTradingAgentPreset = (presetKey) => {
    const preset = AI_TRADING_AGENT_PRESETS[presetKey];
    if (!preset) return;
    setAiTradingAgentSettings((current) => ({
      ...current,
      ...preset,
    }));
    setAiTradingAgentTestResult(null);
  };

  // Context/cadence knobs live in a nested research_context block so the
  // backend can validate and clamp them in one place.
  const handleAiResearchContextField = (field, value) => {
    const numericFields = new Set([
      'market_context_bars',
      'prompt_bars',
      'detailed_markets',
      'market_fetch_bars',
      'opening_range_minutes',
      'model_context_window',
      'limit_order_expiry_seconds',
      'research_interval_minutes',
    ]);
    setAiTradingAgentSettings((current) => ({
      ...current,
      research_context: {
        ...(current?.research_context || {}),
        [field]: numericFields.has(field)
          ? (value === '' || value === null || value === undefined ? '' : Number(value))
          : value,
      },
    }));
  };

  const handleAiResearchTimeframeToggle = (interval) => {
    setAiTradingAgentSettings((current) => {
      const context = current?.research_context || {};
      const ladder = Array.isArray(context.timeframe_ladder) ? context.timeframe_ladder : [];
      const next = ladder.includes(interval)
        ? ladder.filter((value) => value !== interval)
        : [...ladder, interval].sort((a, b) => b - a);
      return { ...current, research_context: { ...context, timeframe_ladder: next } };
    });
  };

  const handleAiTradingAgentField = (field, value) => {
    const numericFields = new Set(['temperature', 'max_tokens', 'timeout_seconds']);
    setAiTradingAgentSettings((current) => ({
      ...current,
      [field]: numericFields.has(field)
        ? (value === '' || value === null || value === undefined ? '' : Number(value))
        : value,
    }));
    setAiTradingAgentTestResult(null);
  };

  const buildAiTradingAgentPayload = useCallback(() => {
    const payload = {
      enabled: Boolean(aiTradingAgentSettings.enabled),
      provider_label: aiTradingAgentSettings.provider_label,
      mode: aiTradingAgentSettings.mode,
      model: aiTradingAgentSettings.model,
      api_base: aiTradingAgentSettings.api_base,
      api_key_env: aiTradingAgentSettings.api_key_env,
      clear_api_key: Boolean(aiTradingAgentSettings.clear_api_key),
      system_prompt: aiTradingAgentSettings.system_prompt,
      temperature: parseAiNumberValue(aiTradingAgentSettings.temperature),
      max_tokens: parseAiNumberValue(aiTradingAgentSettings.max_tokens),
      timeout_seconds: parseAiNumberValue(aiTradingAgentSettings.timeout_seconds),
    };
    if (aiTradingAgentSettings.api_key?.trim()) {
      payload.api_key = aiTradingAgentSettings.api_key.trim();
    }
    return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));
  }, [aiTradingAgentSettings]);

  const handleSaveAiTradingAgentSettings = async () => {
    setSavingAiTradingAgentSettings(true);
    try {
      await updateAITradingAgentSettings(buildAiTradingAgentPayload());
      await reloadAiTradingAgentSettings();
      toast.success('AI trading agent settings updated');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update AI trading agent settings'));
    } finally {
      setSavingAiTradingAgentSettings(false);
    }
  };

  const handleTestAiTradingAgentSettings = async () => {
    setTestingAiTradingAgentSettings(true);
    setAiTradingAgentTestResult(null);
    try {
      const { data } = await testAITradingAgentSettings(buildAiTradingAgentPayload());
      setAiTradingAgentTestResult(data || { ok: true, message: 'Connection successful' });
      toast.success(data?.message || 'Trading agent connection succeeded');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to test AI trading agent');
      setAiTradingAgentTestResult({ ok: false, message });
      toast.error(message);
    } finally {
      setTestingAiTradingAgentSettings(false);
    }
  };

  const addMarketNewsEvent = () => {
    setMarketNewsEvents((current) => [...current, createEmptyNewsEventDraft()]);
  };

  const updateMarketNewsEventField = (eventId, field, value) => {
    setMarketNewsEvents((current) => current.map((event) => {
      if (event.id !== eventId) {
        return event;
      }
      if (field === 'affected_symbols') {
        return {
          ...event,
          affected_symbols: String(value || '')
            .split(',')
            .map((entry) => entry.trim().toUpperCase())
            .filter(Boolean),
        };
      }
      return {
        ...event,
        [field]: value,
      };
    }));
  };

  const removeMarketNewsEvent = (eventId) => {
    setMarketNewsEvents((current) => current.filter((event) => event.id !== eventId));
  };

  const handleSaveMarketNewsEvents = async () => {
    setSavingMarketNewsEvents(true);
    try {
      const payload = {
        events: marketNewsEvents.map((event) => ({
          ...event,
          starts_at: event.starts_at || '',
          affected_symbols: Array.isArray(event.affected_symbols) ? event.affected_symbols : [],
        })),
      };
      const { data } = await updateAdminNewsEvents(payload);
      setMarketNewsEvents(Array.isArray(data?.events) ? data.events : []);
      toast.success('Market news events updated');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save market news events'));
    } finally {
      setSavingMarketNewsEvents(false);
    }
  };

  // Support ticket functions
  const handleUpdateTicket = async (ticketId, status) => {
    try {
      await updateSupportTicket(ticketId, status);
      toast.success('Ticket updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleRetryExecutionJob = async (jobId) => {
    setRetryingExecutionJobId(jobId);
    try {
      await retryExecutionJob(jobId);
      toast.success('Execution job requeued');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to retry execution job'));
    } finally {
      setRetryingExecutionJobId(null);
    }
  };

  const buildDeadLetterCleanupPayload = () => ({
    older_than_hours: Math.max(1, Number(deadLetterCleanupOlderThanHours) || 24),
    include_close: Boolean(deadLetterCleanupIncludeClose),
  });

  const handlePreviewDeadLetterCleanup = async () => {
    setDeadLetterCleanupPreviewing(true);
    setDeadLetterCleanupConfirmation('');
    try {
      const res = await previewDeadLetterCleanup(buildDeadLetterCleanupPayload());
      setDeadLetterCleanupPreview(res.data);
      const total = res.data?.counts?.total_eligible || 0;
      toast.success(`Cleanup preview found ${total} eligible job${total === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to preview dead-letter cleanup'));
    } finally {
      setDeadLetterCleanupPreviewing(false);
    }
  };

  const handleApplyDeadLetterCleanup = async () => {
    if (deadLetterCleanupConfirmation.trim() !== DEAD_LETTER_CLEANUP_CONFIRMATION) {
      toast.error('Type the confirmation phrase before applying cleanup');
      return;
    }
    setDeadLetterCleanupApplying(true);
    try {
      const res = await applyDeadLetterCleanup({
        ...buildDeadLetterCleanupPayload(),
        confirmation: deadLetterCleanupConfirmation.trim(),
      });
      setDeadLetterCleanupPreview(res.data);
      setDeadLetterCleanupConfirmation('');
      await fetchData();
      toast.success(`Deleted ${res.data?.deleted || 0} inactive dead-letter job${(res.data?.deleted || 0) === 1 ? '' : 's'}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to apply dead-letter cleanup'));
    } finally {
      setDeadLetterCleanupApplying(false);
    }
  };

  const renderDeadLetterCleanupCard = () => {
    const counts = deadLetterCleanupPreview?.counts || {};
    const exclusions = deadLetterCleanupPreview?.exclusions || {};
    const totalEligible = counts.total_eligible || 0;
    const canApply = Boolean(deadLetterCleanupPreview)
      && totalEligible > 0
      && deadLetterCleanupConfirmation.trim() === DEAD_LETTER_CLEANUP_CONFIRMATION
      && !deadLetterCleanupPreviewing
      && !deadLetterCleanupApplying;

    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-300" />
                Dead-Letter Cleanup
              </CardTitle>
              <CardDescription>
                Preview old inactive phantom cancel dead letters before any deletion is allowed.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviewDeadLetterCleanup}
              disabled={deadLetterCleanupPreviewing || deadLetterCleanupApplying}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${deadLetterCleanupPreviewing ? 'animate-spin' : ''}`} />
              Preview
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-3">
            <div>
              <label className="text-xs text-zinc-500">Older than hours</label>
              <Input
                type="number"
                min="1"
                value={deadLetterCleanupOlderThanHours}
                onChange={(event) => {
                  setDeadLetterCleanupOlderThanHours(event.target.value);
                  setDeadLetterCleanupConfirmation('');
                }}
                className="mt-1 bg-zinc-800 border-zinc-700"
              />
            </div>
            <label className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-sm text-zinc-300">
              <input
                type="checkbox"
                checked={Boolean(deadLetterCleanupIncludeClose)}
                onChange={(event) => {
                  setDeadLetterCleanupIncludeClose(event.target.checked);
                  setDeadLetterCleanupConfirmation('');
                }}
                className="h-4 w-4 accent-blue-600"
              />
              Include redundant close dead letters only when broker-flat is confirmed and trade is terminal.
            </label>
          </div>

          {deadLetterCleanupPreview ? (
            <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-xs text-zinc-500">Phantom cancel</div>
                  <div className="text-xl font-semibold text-red-300">{counts.phantom_cancel || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Broker-flat close</div>
                  <div className="text-xl font-semibold text-orange-300">{counts.redundant_close_broker_flat || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">Total eligible</div>
                  <div className="text-xl font-semibold text-white">{totalEligible}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-zinc-400">
                <span>Too recent: {exclusions.too_recent || 0}</span>
                <span>Active trade: {exclusions.linked_trade_active_open || 0}</span>
                <span>Active broker position: {exclusions.linked_trade_active_broker_position || 0}</span>
                <span>Wrong cancel error: {exclusions.cancel_wrong_error_pattern || 0}</span>
              </div>
              {totalEligible > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-2 text-xs text-amber-100">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Type <span className="font-mono text-white">{DEAD_LETTER_CLEANUP_CONFIRMATION}</span> to apply the previewed cleanup.</span>
                  </div>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <Input
                      value={deadLetterCleanupConfirmation}
                      onChange={(event) => setDeadLetterCleanupConfirmation(event.target.value)}
                      placeholder={DEAD_LETTER_CLEANUP_CONFIRMATION}
                      className="bg-zinc-800 border-zinc-700 font-mono"
                    />
                    <Button
                      variant="destructive"
                      onClick={handleApplyDeadLetterCleanup}
                      disabled={!canApply}
                    >
                      <Trash2 className={`w-4 h-4 mr-2 ${deadLetterCleanupApplying ? 'animate-pulse' : ''}`} />
                      Apply Cleanup
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500">No eligible inactive dead-letter jobs matched the current filters.</div>
              )}
            </div>
          ) : (
            <div className="text-sm text-zinc-500">Run Preview to see eligible categories before deletion.</div>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleRunMarketRefresh = async () => {
    const symbol = marketRefreshForm.symbol?.trim().toUpperCase();
    const csvPath = marketRefreshForm.csv_path?.trim();
    if (!symbol) {
      toast.error('Select a symbol');
      return;
    }
    if (!marketDataSymbolOptions.some((option) => option.value === symbol)) {
      toast.error('Selected symbol is not in the current system symbol list');
      return;
    }
    if (!csvPath) {
      toast.error('CSV path is required');
      return;
    }

    const timeframes = (marketRefreshForm.timeframes || '')
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);

    if (!timeframes.length) {
      toast.error('At least one timeframe is required');
      return;
    }

    setRunningMarketRefresh(true);
    try {
      const payload = {
        provider: marketRefreshForm.provider || 'dukascopy',
        symbol,
        csv_path: csvPath,
        source_timezone: marketRefreshForm.source_timezone || 'UTC',
        session_type: marketRefreshForm.session_type || 'no_session',
        mode: marketRefreshForm.mode || 'incremental',
        timeframes,
      };
      const res = await triggerMarketDataRefresh(payload);
      toast.success(`Refresh queued (${res.data?.job_id?.slice(0, 8) || 'job'})`);
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to queue market data refresh'));
    } finally {
      setRunningMarketRefresh(false);
    }
  };

  const handleBrokerGapRepairField = (field, value) => {
    setBrokerGapRepairForm((current) => ({ ...current, [field]: value }));
    setBrokerGapRepairPreview(null);
    setBrokerGapRepairConfirmation('');
  };

  const handlePreviewBrokerGapRepair = async () => {
    const symbol = brokerGapRepairForm.symbol?.trim().toUpperCase();
    const startTime = dateTimeLocalInputElementToIso(
      brokerGapRepairStartInputRef.current,
      brokerGapRepairForm.start_time,
    );
    const endTime = dateTimeLocalInputElementToIso(
      brokerGapRepairEndInputRef.current,
      brokerGapRepairForm.end_time,
    );
    const maximumBars = Number(brokerGapRepairForm.maximum_bars);
    if (!symbol) {
      toast.error('Select a symbol');
      return;
    }
    if (!startTime) {
      toast.error('Select a valid start time');
      return;
    }
    if (!endTime) {
      toast.error('Select a valid end time');
      return;
    }
    if (!Number.isInteger(maximumBars) || maximumBars < 60 || maximumBars > 30000) {
      toast.error('Maximum bars must be a whole number between 60 and 30,000');
      return;
    }
    setPreviewingBrokerGapRepair(true);
    try {
      const response = await previewBrokerGapRepair({
        symbol,
        start_time: startTime,
        end_time: endTime,
        maximum_bars: maximumBars,
      });
      setBrokerGapRepairPreview(response.data || null);
      setBrokerGapRepairConfirmation('');
      toast.success('Gap repair preview is ready');
    } catch (error) {
      setBrokerGapRepairPreview(null);
      toast.error(getApiErrorMessage(error, 'Failed to preview broker gap repair'));
    } finally {
      setPreviewingBrokerGapRepair(false);
    }
  };

  const handleApplyBrokerGapRepair = async () => {
    if (!brokerGapRepairPreview?.preview_token) {
      toast.error('Run a fresh preview first');
      return;
    }
    setApplyingBrokerGapRepair(true);
    try {
      const response = await applyBrokerGapRepair({
        preview_token: brokerGapRepairPreview.preview_token,
        confirmation: brokerGapRepairConfirmation,
      });
      toast.success(`Broker gap repair queued (${response.data?.job_id?.slice(0, 8) || 'job'})`);
      setBrokerGapRepairPreview(null);
      setBrokerGapRepairConfirmation('');
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to queue broker gap repair'));
    } finally {
      setApplyingBrokerGapRepair(false);
    }
  };

  const loadMarketBrowser = useCallback(async (path = '') => {
    setMarketBrowserLoading(true);
    try {
      const selectedSymbol = marketRefreshForm.symbol?.trim().toUpperCase();
      const res = await getMarketDataBrowser({
        ...(path ? { path } : {}),
        ...(selectedSymbol ? { symbol: selectedSymbol } : {}),
      });
      setMarketBrowserRoots(res.data?.roots || []);
      setMarketBrowserEntries(res.data?.entries || []);
      setMarketBrowserCurrentPath(res.data?.current_path || '');
      setMarketBrowserParentPath(res.data?.parent_path || null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load server folders'));
    } finally {
      setMarketBrowserLoading(false);
    }
  }, [marketRefreshForm.symbol]);

  const openMarketBrowser = async () => {
    setShowMarketBrowserDialog(true);
    await loadMarketBrowser(marketRefreshForm.csv_path || '');
  };

  const selectMarketDataFile = (path) => {
    setMarketRefreshForm((current) => ({ ...current, csv_path: path }));
    setShowMarketBrowserDialog(false);
    toast.success('CSV path selected');
  };

  const formatFileSize = (sizeBytes) => {
    if (sizeBytes == null) return '';
    if (sizeBytes < 1024) return `${sizeBytes} B`;
    if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
    if (sizeBytes < 1024 * 1024 * 1024) return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const handleMarketRefreshSymbolChange = (value) => {
    setMarketRefreshForm((current) => {
      const currentFilename = (current.csv_path || '').split(/[\\/]/).pop() || '';
      const normalizedFilename = currentFilename.replace(/[^a-z0-9]/gi, '').toUpperCase();
      return {
        ...current,
        symbol: value,
        csv_path: normalizedFilename.includes(value) ? current.csv_path : '',
      };
    });
  };

  const updateBrokerDraft = (brokerId, updater) => {
    setBrokerInstrumentDrafts((current) => {
      const existing = current[brokerId] || [];
      const next = typeof updater === 'function' ? updater(existing) : updater;
      return { ...current, [brokerId]: next };
    });
  };

  const updateBrokerConfigField = (brokerId, field, value) => {
    setAdminBrokers((current) => current.map((entry) => (
      entry.id === brokerId ? { ...entry, [field]: value } : entry
    )));
  };

  const updateBrokerTradingHoursField = (brokerId, field, value) => {
    setAdminBrokers((current) => current.map((entry) => (
      entry.id === brokerId
        ? {
            ...entry,
            trading_hours: {
              ...(entry.trading_hours || {}),
              [field]: value,
            },
          }
        : entry
    )));
  };

  const buildBrokerAdminPayload = (broker, draft) => ({
    supported_instruments: draft.map((entry) => entry.symbol),
    instruments: draft,
    is_active: broker.is_active,
    default_trading_hours_start: broker.trading_hours?.start || '',
    default_trading_hours_end: broker.trading_hours?.end || '',
    timezone: broker.trading_hours?.timezone || '',
    enforce_trading_hours: Boolean(broker.enforce_trading_hours),
    close_at_end_of_session: Boolean(broker.close_at_end_of_session),
    trading_hours_open_days: broker.trading_hours?.open_days ?? null,
    trading_hours_close_days: broker.trading_hours?.close_days ?? null,
  });

  const updateBrokerInstrumentField = (brokerId, symbol, field, value) => {
    updateBrokerDraft(brokerId, (current) => current.map((entry) => (
      entry.symbol === symbol ? { ...entry, [field]: value } : entry
    )));
  };

  const addBrokerInstrument = (brokerId, value) => {
    const option = buildInstrumentOption(value);
    if (!option) return;
    updateBrokerDraft(brokerId, (current) => (
      current.some((entry) => entry.symbol === option.value)
        ? current
        : [
            ...current,
            {
              symbol: option.value,
              display_label: option.label,
              display_name: option.name,
              instrument_type: ['BTCUSD', 'ETHUSD'].includes(option.value)
                ? 'crypto'
                : option.value === 'EURUSD'
                  ? 'forex'
                  : 'futures',
              symbol_id: '',
              contract_id: '',
              aliases: [],
              sort_order: current.length,
              is_active: true,
            },
          ]
    ));
    setBrokerInstrumentInputs((current) => ({ ...current, [brokerId]: '' }));
  };

  const removeBrokerInstrument = (brokerId, symbol) => {
    updateBrokerDraft(brokerId, (current) => current.filter((entry) => entry.symbol !== symbol));
  };

  const handleSaveBroker = async (broker) => {
    setSavingBrokerId(broker.id);
    try {
      const draft = brokerInstrumentDrafts[broker.id] || [];
      const payload = buildBrokerAdminPayload(broker, draft);
      const res = await updateAdminBroker(broker.id, payload);
      const updatedBroker = res.data?.broker;
      setAdminBrokers((current) => current.map((entry) => (
        entry.id === broker.id ? { ...entry, ...updatedBroker } : entry
      )));
      setBrokerInstrumentDrafts((current) => ({
        ...current,
        [broker.id]: updatedBroker?.instruments || draft,
      }));
      toast.success('Broker settings updated');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update broker settings'));
    } finally {
      setSavingBrokerId(null);
    }
  };

  const handleToggleBrokerActive = async (broker, nextActive) => {
    setSavingBrokerId(broker.id);
    try {
      const draft = brokerInstrumentDrafts[broker.id] || [];
      const payload = {
        ...buildBrokerAdminPayload(broker, draft),
        is_active: nextActive,
      };
      const res = await updateAdminBroker(broker.id, payload);
      const updatedBroker = res.data?.broker;
      setAdminBrokers((current) => current.map((entry) => (
        entry.id === broker.id ? { ...entry, ...updatedBroker } : entry
      )));
      setBrokerInstrumentDrafts((current) => ({
        ...current,
        [broker.id]: updatedBroker?.instruments || draft,
      }));
      toast.success(nextActive ? 'Broker enabled' : 'Broker disabled');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update broker status'));
    } finally {
      setSavingBrokerId(null);
    }
  };

  const handleSaveMarketSessionConfig = async () => {
    setSavingMarketSessionConfig(true);
    try {
      const parsed = JSON.parse(marketSessionConfigDraft || '{}');
      const { data } = await updateAdminMarketSessionConfig({ config: parsed });
      setMarketSessionConfigDraft(JSON.stringify(data?.config || parsed, null, 2));
      toast.success('Market session policies updated');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update market session config'));
    } finally {
      setSavingMarketSessionConfig(false);
    }
  };

  const handleUpdateSystemSettings = async () => {
    setSavingSystemSettings(true);
    try {
      await updateSystemSettings(systemSettings);
      toast.success('System settings updated');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update system settings'));
    } finally {
      setSavingSystemSettings(false);
    }
  };

  const handleAddNotificationSummaryTime = () => {
    if (!newNotificationSummaryTime) return;
    if (notificationSummaryTimes.includes(newNotificationSummaryTime)) {
      toast.error('That summary time already exists');
      return;
    }
    setNotificationSummaryTimes((current) => [...current, newNotificationSummaryTime].sort());
    setNewNotificationSummaryTime('');
  };

  const handleSaveNotificationSummaryTimes = async () => {
    setSavingNotificationSummaryTimes(true);
    try {
      const { data } = await updateNotificationSummarySchedule(notificationSummaryTimes);
      setNotificationSummaryTimes(Array.isArray(data?.times) ? data.times : []);
      toast.success('Summary alert schedule updated');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update summary alert schedule'));
    } finally {
      setSavingNotificationSummaryTimes(false);
    }
  };

  const renderNotificationSummarySchedule = () => (
    <div className="space-y-4 border-t border-zinc-800 pt-4">
      <div>
        <h3 className="text-md font-medium text-zinc-300">Futures Summary Alert Schedule</h3>
        <p className="mt-1 text-xs text-zinc-500">
          Weekdays only. All times use America/New_York and automatically follow daylight saving time.
        </p>
      </div>
      <div className="space-y-2">
        {notificationSummaryTimes.length === 0 && (
          <div className="rounded-md border border-dashed border-zinc-700 p-3 text-sm text-zinc-500">
            No summary alerts are scheduled.
          </div>
        )}
        {notificationSummaryTimes.map((timeValue, index) => (
          <div key={`${timeValue}-${index}`} className="flex items-center gap-2">
            <Input
              type="time"
              value={timeValue}
              onChange={(event) => setNotificationSummaryTimes((current) => (
                current.map((value, itemIndex) => itemIndex === index ? event.target.value : value)
              ))}
              className="max-w-48 bg-zinc-800 border-zinc-700"
              aria-label={`Summary time ${index + 1}`}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setNotificationSummaryTimes((current) => current.filter((_, itemIndex) => itemIndex !== index))}
              aria-label={`Remove summary time ${timeValue}`}
              title="Remove time"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="time"
          value={newNotificationSummaryTime}
          onChange={(event) => setNewNotificationSummaryTime(event.target.value)}
          className="max-w-48 bg-zinc-800 border-zinc-700"
          aria-label="New summary time"
        />
        <Button type="button" variant="outline" onClick={handleAddNotificationSummaryTime} disabled={!newNotificationSummaryTime}>
          Add Time
        </Button>
        <Button type="button" onClick={handleSaveNotificationSummaryTimes} disabled={savingNotificationSummaryTimes}>
          {savingNotificationSummaryTimes ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save Schedule'}
        </Button>
      </div>
    </div>
  );

  const openSupportQueueCount = useMemo(
    () => tickets.filter((ticket) => ticket.status !== 'resolved').length,
    [tickets],
  );

  const activeBrokerCount = useMemo(
    () => adminBrokers.filter((broker) => broker.is_active).length,
    [adminBrokers],
  );

  const renderAdminQuickLinks = (compact = false) => (
    <div className={`mb-8 grid grid-cols-1 gap-4 ${compact ? '' : 'md:grid-cols-2'}`}>
      <button
        type="button"
        onClick={() => navigate('/subscription')}
        className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 text-left transition hover:border-blue-500/40 hover:bg-zinc-900/90"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-500/10 p-2 text-blue-300">
            <CreditCard className="h-4 w-4" />
          </div>
          <div>
            <div className="font-semibold text-white">Open Subscription</div>
            <div className="text-sm text-zinc-500">Review plans, access, and billing-facing flows.</div>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={() => navigate('/onboarding/broker-connect')}
        className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 text-left transition hover:border-blue-500/40 hover:bg-zinc-900/90"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-500/10 p-2 text-blue-300">
            <Activity className="h-4 w-4" />
          </div>
          <div>
            <div className="font-semibold text-white">Open Broker Connect</div>
            <div className="text-sm text-zinc-500">Jump straight to broker onboarding and connection management.</div>
          </div>
        </div>
      </button>
    </div>
  );

  const renderAdminStatsGrid = (compact = false) => (
    <div className={`mb-8 grid grid-cols-1 gap-4 ${compact ? 'sm:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4 gap-6'}`}>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Total Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
          <p className="text-xs text-green-400">+{stats?.new_users_this_month || 0} this month</p>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Active Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{stats?.active_subscriptions || 0}</div>
          <p className="text-xs text-zinc-500">Paid plans</p>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Monthly Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-400">
            ${stats?.revenue_monthly?.toFixed(2) || '0.00'}
          </div>
          <p className="text-xs text-zinc-500">Last 30 days</p>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Total Revenue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">${stats?.revenue_total?.toFixed(2) || '0.00'}</div>
          <p className="text-xs text-zinc-500">All time</p>
        </CardContent>
      </Card>
    </div>
  );

  const renderPlanDistribution = () => (
    <Card className="bg-zinc-900 border-zinc-800 mb-8">
      <CardHeader>
        <CardTitle className="text-sm text-zinc-400">Users by Plan</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 flex-wrap">
          {stats?.users_by_plan && Object.entries(stats.users_by_plan).map(([plan, count]) => (
            <div key={plan} className="flex items-center gap-2">
              <Badge variant={plan === 'FREE' ? 'secondary' : plan === 'PRO' ? 'default' : 'outline'}>
                {plan}
              </Badge>
              <span className="text-lg font-semibold">{count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderSystemSettingsPanel = (compact = false) => (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Platform System Settings
        </CardTitle>
        <CardDescription>Global configuration for platform behavior and performance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className={`grid grid-cols-1 gap-6 ${compact ? '' : 'md:grid-cols-2'}`}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-400">Dashboard Data Refresh Rate (ms)</label>
            <div className="flex gap-3">
              <Input
                type="number"
                value={systemSettings.data_refresh_rate_ms}
                onChange={(e) => setSystemSettings({ ...systemSettings, data_refresh_rate_ms: parseInt(e.target.value, 10) })}
                className="bg-zinc-800 border-zinc-700"
              />
              <Button
                onClick={handleUpdateSystemSettings}
                disabled={savingSystemSettings}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingSystemSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Controls how often the dashboard UI polls for price updates and account state.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-400">Payment Enforcement</label>
            <div className="flex items-center justify-between rounded-lg border border-zinc-700 bg-zinc-800/50 p-3">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">Require Active Subscription</div>
                <div className="text-xs text-zinc-500">Gate features based on plan limits</div>
              </div>
              <Switch
                checked={systemSettings.payments_enforced}
                onCheckedChange={(checked) => {
                  const next = { ...systemSettings, payments_enforced: checked };
                  setSystemSettings(next);
                  setPricingSettings({ payments_enforced: checked });
                  updateSystemSettings(next).then(() => {
                    toast.success('Payment enforcement updated');
                    fetchData();
                  });
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-400">Live Order Quote Guardrail</label>
            <div className="flex items-center justify-between rounded-lg border border-zinc-700 bg-zinc-800/50 p-3">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">Require Fresh Broker Quote</div>
                <div className="text-xs text-zinc-500">Block live orders when the latest quote is missing or stale.</div>
              </div>
              <Switch
                checked={Boolean(systemSettings.live_order_require_fresh_quote)}
                onCheckedChange={(checked) => setSystemSettings({
                  ...systemSettings,
                  live_order_require_fresh_quote: checked,
                })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-400">Live Order Quote Max Age (seconds)</label>
            <div className="flex gap-3">
              <Input
                type="number"
                min="2"
                max="300"
                step="1"
                value={systemSettings.live_order_quote_max_age_seconds}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  live_order_quote_max_age_seconds: Number.parseFloat(e.target.value) || 2,
                })}
                className="bg-zinc-800 border-zinc-700"
              />
              <Button
                onClick={handleUpdateSystemSettings}
                disabled={savingSystemSettings}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingSystemSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Admin-controlled freshness threshold used before a live order can be queued. Default is 12 seconds.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-400">Signal Recipient Activity Window (days)</label>
            <div className="flex gap-3">
              <Input
                type="number"
                min="1"
                max="3650"
                step="1"
                value={systemSettings.signal_recipient_active_days}
                onChange={(e) => setSystemSettings({
                  ...systemSettings,
                  signal_recipient_active_days: Number.parseInt(e.target.value, 10) || 1,
                })}
                className="bg-zinc-800 border-zinc-700"
              />
              <Button
                onClick={handleUpdateSystemSettings}
                disabled={savingSystemSettings}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingSystemSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Send strategy signal push and email alerts only to users who signed in within this window.
            </p>
          </div>
        </div>

        {renderNotificationSummarySchedule()}

        <div className="space-y-4 border-t border-zinc-800 pt-4">
          <h3 className="text-md font-medium text-zinc-300">Integrations</h3>
          <div className={`grid grid-cols-1 gap-6 ${compact ? '' : 'md:grid-cols-2'}`}>
            <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-zinc-400" />
                  <div>
                    <div className="font-medium">Email Service (Resend)</div>
                    <div className="text-xs text-zinc-500">Transactional emails & alerts</div>
                  </div>
                </div>
                <Badge variant={resendStatus?.status === 'connected' ? 'default' : 'destructive'}>
                  {resendStatus?.status === 'connected' ? 'Connected' : 'Error'}
                </Badge>
              </div>

              {resendStatus?.status === 'connected' ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-zinc-500">Sender:</span>
                    <span className="break-all font-mono text-zinc-300">{resendStatus.sender_email}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-zinc-500">Verified Domains:</span>
                    <span className="text-zinc-300">{resendStatus.active_domains} / {resendStatus.verified_domains}</span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-400">
                  {resendStatus?.message || 'Configuration missing or API key invalid'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4 border-t border-zinc-800 pt-4">
          <h3 className="text-md font-medium text-red-400 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Danger Zone
          </h3>
          <div className="rounded-lg border border-red-900/30 bg-red-950/10 p-4">
            <div className={`flex gap-4 ${compact ? 'flex-col' : 'items-center justify-between'}`}>
              <div>
                <div className="font-bold text-red-500">Global Kill Switch</div>
                <div className="mt-1 max-w-md text-sm text-zinc-400">
                  Immediately blocks all new orders and attempts to cancel all open orders across the entire platform.
                </div>
              </div>
              <Button
                variant={systemSettings.kill_switch_enabled ? "destructive" : "outline"}
                className={systemSettings.kill_switch_enabled
                  ? "bg-red-600 hover:bg-red-700 border-red-500"
                  : "border-red-900/50 text-red-500 hover:bg-red-900/20"}
                onClick={() => setShowKillSwitchConfirm(true)}
              >
                {systemSettings.kill_switch_enabled ? "DEACTIVATE KILL SWITCH" : "ACTIVATE KILL SWITCH"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderAdminMobileOverview = () => (
    <div className="space-y-4">
      {systemSettings.kill_switch_enabled ? (
        <div className="rounded-lg border border-red-800 bg-red-900/30 p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Octagon className="h-7 w-7 text-red-500" />
              <div>
                <h3 className="font-bold text-red-400">System Kill Switch Active</h3>
                <p className="text-sm text-red-300">All new orders are blocked. Open orders have been cancelled.</p>
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowKillSwitchConfirm(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              Deactivate Emergency Stop
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="border-red-900/50 text-red-500 hover:bg-red-900/20 hover:text-red-400"
            onClick={() => setShowKillSwitchConfirm(true)}
          >
            <Octagon className="mr-2 h-4 w-4" />
            Emergency Stop
          </Button>
        </div>
      )}

      {adminLoadWarnings.length ? (
        <Card className="border-amber-500/20 bg-amber-500/10">
          <CardContent className="flex flex-col gap-3 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <div>
                <div className="font-medium text-amber-100">Admin data loaded with fallbacks</div>
                <p className="text-sm text-amber-200/80">
                  Some sections could not be refreshed and are showing safe defaults:
                  {' '}
                  {adminLoadWarnings.join(', ')}.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchData} className="border-amber-400/30 text-amber-100 hover:bg-amber-500/10">
              Retry Load
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {renderAdminQuickLinks(true)}
      {renderAdminStatsGrid(true)}

      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">AI Provider</div>
            <div className="mt-2 text-base font-semibold text-white">{aiAnalysisSettings.provider_label || 'Disabled'}</div>
            <div className="mt-1 text-xs text-zinc-500">
              {aiAnalysisSettings.enabled ? 'LLM-backed analysis enabled' : 'Rule-based fallback only'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Brokers</div>
            <div className="mt-2 text-base font-semibold text-white">{activeBrokerCount} active / {adminBrokers.length} total</div>
            <div className="mt-1 text-xs text-zinc-500">Broker availability at a glance</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Support Queue</div>
            <div className="mt-2 text-base font-semibold text-white">{openSupportQueueCount} open</div>
            <div className="mt-1 text-xs text-zinc-500">{tickets.length} total ticket(s)</div>
          </CardContent>
        </Card>
        <Card className={Number(executionOverview.jobs?.dead_letter || 0) > 0 ? "border-red-500/35 bg-red-500/10" : "bg-zinc-900 border-zinc-800"}>
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Execution Queue</div>
            <div className="mt-2 text-base font-semibold text-white">{executionOverview.jobs?.queued || 0} queued</div>
            <div className={Number(executionOverview.jobs?.dead_letter || 0) > 0 ? "mt-1 text-xs font-medium text-red-300" : "mt-1 text-xs text-zinc-500"}>
              {executionOverview.jobs?.dead_letter || 0} dead letters
            </div>
          </CardContent>
        </Card>
      </div>

      {renderPlanDistribution()}
    </div>
  );

  const renderAdminMobileSectionTabs = () => (
    <div className="space-y-2">
      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
        Admin workspace
      </label>
      <Select value={mobileAdminSection} onValueChange={setMobileAdminSection}>
        <SelectTrigger className="h-12 w-full border-zinc-700 bg-zinc-900 text-white" aria-label="Admin workspace">
          <SelectValue placeholder="Choose an admin workspace" />
        </SelectTrigger>
        <SelectContent className="border-zinc-700 bg-zinc-950 text-white">
          <SelectItem value="overview">Overview</SelectItem>
          <SelectItem value="users">Users</SelectItem>
          <SelectItem value="pricing">Billing</SelectItem>
          <SelectItem value="brokers">Brokers</SelectItem>
          <SelectItem value="support">Support</SelectItem>
          <SelectItem value="more">Operations, AI, Strategies, and System</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const renderAdminMobileMore = () => (
    <Accordion type="multiple" defaultValue={['ai']} className="space-y-3">
      <AccordionItem value="ai" className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 px-0">
        <AccordionTrigger className="px-4 py-4 text-left text-white hover:no-underline">
          <div>
            <div className="text-sm font-semibold text-white">AI & Market Events</div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Provider, prompt, and news controls</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <AITab
            aiAnalysisSettings={aiAnalysisSettings}
            aiProviderPresetKey={aiProviderPresetKey}
            selectedAiProviderPreset={selectedAiProviderPreset}
            apiKeyEnvLooksLikeSecret={apiKeyEnvLooksLikeSecret}
            savingAiSettings={savingAiSettings}
            testingAiSettings={testingAiSettings}
            aiSettingsTestResult={aiSettingsTestResult}
            appTimeZone={appTimeZone}
            handleAiSettingsField={handleAiSettingsField}
            applyAiProviderPreset={applyAiProviderPreset}
            handleSaveAiSettings={handleSaveAiSettings}
            handleTestAiSettings={handleTestAiSettings}
            aiTradingAgentSettings={aiTradingAgentSettings}
            aiTradingAgentPresetKey={aiTradingAgentPresetKey}
            selectedAiTradingAgentPreset={selectedAiTradingAgentPreset}
            agentApiKeyEnvLooksLikeSecret={agentApiKeyEnvLooksLikeSecret}
            savingAiTradingAgentSettings={savingAiTradingAgentSettings}
            testingAiTradingAgentSettings={testingAiTradingAgentSettings}
            aiTradingAgentTestResult={aiTradingAgentTestResult}
            handleAiTradingAgentField={handleAiTradingAgentField}
            handleAiResearchContextField={handleAiResearchContextField}
            handleAiResearchTimeframeToggle={handleAiResearchTimeframeToggle}
            applyAiTradingAgentPreset={applyAiTradingAgentPreset}
            handleSaveAiTradingAgentSettings={handleSaveAiTradingAgentSettings}
            handleTestAiTradingAgentSettings={handleTestAiTradingAgentSettings}
            marketNewsEvents={marketNewsEvents}
            savingMarketNewsEvents={savingMarketNewsEvents}
            addMarketNewsEvent={addMarketNewsEvent}
            removeMarketNewsEvent={removeMarketNewsEvent}
            updateMarketNewsEventField={updateMarketNewsEventField}
            handleSaveMarketNewsEvents={handleSaveMarketNewsEvents}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="operations" className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 px-0">
        <AccordionTrigger className="px-4 py-4 text-left text-white hover:no-underline">
          <div>
            <div className="text-sm font-semibold text-white">Operations</div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Queues, retries, and data refresh</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <OperationsTab
            executionOverview={executionOverview}
            executionJobs={executionJobs}
            retryingExecutionJobId={retryingExecutionJobId}
            marketDataJobs={marketDataJobs}
            marketDataCoverage={marketDataCoverage}
            marketDataSymbolOptions={marketDataSymbolOptions}
            marketRefreshForm={marketRefreshForm}
            setMarketRefreshForm={setMarketRefreshForm}
            runningMarketRefresh={runningMarketRefresh}
            brokerGapRepairForm={brokerGapRepairForm}
            brokerGapRepairStartInputRef={brokerGapRepairStartInputRef}
            brokerGapRepairEndInputRef={brokerGapRepairEndInputRef}
            brokerGapRepairPreview={brokerGapRepairPreview}
            brokerGapRepairConfirmation={brokerGapRepairConfirmation}
            setBrokerGapRepairConfirmation={setBrokerGapRepairConfirmation}
            previewingBrokerGapRepair={previewingBrokerGapRepair}
            applyingBrokerGapRepair={applyingBrokerGapRepair}
            appTimeZone={appTimeZone}
            fetchData={fetchData}
            handleRetryExecutionJob={handleRetryExecutionJob}
            deadLetterCleanupPreview={deadLetterCleanupPreview}
            deadLetterCleanupOlderThanHours={deadLetterCleanupOlderThanHours}
            setDeadLetterCleanupOlderThanHours={setDeadLetterCleanupOlderThanHours}
            deadLetterCleanupIncludeClose={deadLetterCleanupIncludeClose}
            setDeadLetterCleanupIncludeClose={setDeadLetterCleanupIncludeClose}
            deadLetterCleanupConfirmation={deadLetterCleanupConfirmation}
            setDeadLetterCleanupConfirmation={setDeadLetterCleanupConfirmation}
            deadLetterCleanupPreviewing={deadLetterCleanupPreviewing}
            deadLetterCleanupApplying={deadLetterCleanupApplying}
            deadLetterCleanupConfirmationPhrase={DEAD_LETTER_CLEANUP_CONFIRMATION}
            handlePreviewDeadLetterCleanup={handlePreviewDeadLetterCleanup}
            handleApplyDeadLetterCleanup={handleApplyDeadLetterCleanup}
            handleRunMarketRefresh={handleRunMarketRefresh}
            handleBrokerGapRepairField={handleBrokerGapRepairField}
            handlePreviewBrokerGapRepair={handlePreviewBrokerGapRepair}
            handleApplyBrokerGapRepair={handleApplyBrokerGapRepair}
            handleMarketRefreshSymbolChange={handleMarketRefreshSymbolChange}
            openMarketBrowser={openMarketBrowser}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="strategies" className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 px-0">
        <AccordionTrigger className="px-4 py-4 text-left text-white hover:no-underline">
          <div>
            <div className="text-sm font-semibold text-white">Strategies</div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Catalog and runtime configuration</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          <StrategiesTab
            newStrategy={newStrategy}
            setNewStrategy={setNewStrategy}
            catalogStrategies={catalogStrategies}
            fetchCatalogStrategies={fetchCatalogStrategies}
            handleCreateCatalogStrategy={handleCreateCatalogStrategy}
            openEditStrategy={openEditStrategy}
            requestToggleCatalogStrategy={requestToggleCatalogStrategy}
            requestDeleteCatalogStrategy={requestDeleteCatalogStrategy}
            updateCatalogRuntimeEngine={updateCatalogRuntimeEngine}
            updateCatalogRuntimeParam={updateCatalogRuntimeParam}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="system" className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 px-0">
        <AccordionTrigger className="px-4 py-4 text-left text-white hover:no-underline">
          <div>
            <div className="text-sm font-semibold text-white">System</div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Refresh rate, payments, and kill switch</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          {renderSystemSettingsPanel(true)}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  const renderAdminMobileSectionContent = () => {
    if (mobileAdminSection === 'overview') {
      return renderAdminMobileOverview();
    }
    if (mobileAdminSection === 'users') {
      return (
        <UsersTab
          users={users}
          totalUsers={totalUsers}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          planFilter={planFilter}
          setPlanFilter={setPlanFilter}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          appTimeZone={appTimeZone}
          handleViewUser={handleViewUser}
          handleSendPasswordReset={handleSendPasswordReset}
          handleDeleteUser={handleDeleteUser}
          mobile
        />
      );
    }
    if (mobileAdminSection === 'pricing') {
      return (
        <PricingTab
          pricing={pricing}
          pricingSettings={pricingSettings}
          savingPricingSettings={savingPricingSettings}
          togglePricingEnforcement={togglePricingEnforcement}
          handleTogglePricingEnforcement={handleTogglePricingEnforcement}
          handleEditPricing={handleEditPricing}
        />
      );
    }
    if (mobileAdminSection === 'brokers') {
      return (
        <BrokersTab
          adminBrokers={adminBrokers}
          brokerInstrumentDrafts={brokerInstrumentDrafts}
          brokerInstrumentInputs={brokerInstrumentInputs}
          setBrokerInstrumentInputs={setBrokerInstrumentInputs}
          savingBrokerId={savingBrokerId}
          marketSessionConfigDraft={marketSessionConfigDraft}
          setMarketSessionConfigDraft={setMarketSessionConfigDraft}
          savingMarketSessionConfig={savingMarketSessionConfig}
          handleToggleBrokerActive={handleToggleBrokerActive}
          handleSaveBroker={handleSaveBroker}
          addBrokerInstrument={addBrokerInstrument}
          removeBrokerInstrument={removeBrokerInstrument}
          updateBrokerInstrumentField={updateBrokerInstrumentField}
          handleSaveMarketSessionConfig={handleSaveMarketSessionConfig}
        />
      );
    }
    if (mobileAdminSection === 'support') {
      return (
        <SupportTab
          tickets={tickets}
          appTimeZone={appTimeZone}
          handleUpdateTicket={handleUpdateTicket}
          mobile
        />
      );
    }
    return renderAdminMobileMore();
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (isMobile) {
    return (
      <PlatformShell title="Admin Dashboard" titleIcon={Shield} backTo="/dashboard" currentKey="admin" mainClassName="overflow-y-auto">
        <main className="mx-auto max-w-7xl px-4 py-6">
          <div className="space-y-4">
            {renderAdminMobileSectionTabs()}
            {renderAdminMobileSectionContent()}
          </div>
        </main>

        <Dialog open={showKillSwitchConfirm} onOpenChange={setShowKillSwitchConfirm}>
          <DialogContent className="bg-zinc-900 border-red-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-red-500 flex items-center gap-2 text-xl">
                <Octagon className="w-6 h-6" />
                {systemSettings.kill_switch_enabled ? "Deactivate Kill Switch?" : "ACTIVATE GLOBAL KILL SWITCH?"}
              </DialogTitle>
            </DialogHeader>

            <div className="py-4 space-y-4">
              {systemSettings.kill_switch_enabled ? (
                <p className="text-zinc-300">
                  This will resume normal trading operations. Users will be able to place orders again immediately.
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="font-bold text-white text-lg">⚠️ EXTREME CAUTION REQUIRED</p>
                  <ul className="list-disc list-inside space-y-2 text-zinc-300">
                    <li>All <strong>new order submissions</strong> will be blocked immediately.</li>
                    <li>The system will attempt to <strong>cancel ALL open orders</strong> on all broker connections.</li>
                    <li>This action affects <strong>ALL USERS</strong> on the platform.</li>
                  </ul>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowKillSwitchConfirm(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleToggleKillSwitch}
                disabled={killSwitchProcessing}
                className="bg-red-600 hover:bg-red-700"
              >
                {killSwitchProcessing
                  ? <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  : (systemSettings.kill_switch_enabled ? "Resume Trading" : "ACTIVATE KILL SWITCH")
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-500">Email</label>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-500">Name</label>
                    <p className="font-medium">{selectedUser.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-500">Role</label>
                    <Select
                      value={selectedUser.role}
                      onValueChange={(value) => setSelectedUser({...selectedUser, role: value})}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-500">Subscription Plan</label>
                    <Select
                      value={selectedUser.subscription_plan}
                      onValueChange={(value) => setSelectedUser({...selectedUser, subscription_plan: value})}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="FREE">FREE</SelectItem>
                        <SelectItem value="BASIC">BASIC</SelectItem>
                        <SelectItem value="PRO">PRO</SelectItem>
                        <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-500">Status</label>
                    <Select
                      value={selectedUser.subscription_status}
                      onValueChange={(value) => setSelectedUser({...selectedUser, subscription_status: value})}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                        <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                        <SelectItem value="CANCELED">CANCELED</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-500">Email Verified</label>
                    <div className="mt-2">
                      <Switch
                        checked={selectedUser.email_verified}
                        onCheckedChange={(checked) => setSelectedUser({...selectedUser, email_verified: checked})}
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
                  <Button onClick={handleUpdateUser} disabled={savingUser}>
                    {savingUser ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Pricing Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-500">Plan Name</label>
                <Input
                  value={editedPricing.name || ''}
                  onChange={(e) => setEditedPricing({...editedPricing, name: e.target.value})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Monthly Price ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editedPricing.price_monthly ?? ''}
                  onChange={(e) => setEditedPricing({...editedPricing, price_monthly: parseFloat(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Yearly Price ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editedPricing.price_yearly ?? ''}
                  onChange={(e) => setEditedPricing({...editedPricing, price_yearly: parseFloat(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm text-zinc-500">Max Broker Accounts</label>
                  <Input
                    type="number"
                    value={editedPricing.max_accounts ?? ''}
                    onChange={(e) => setEditedPricing({...editedPricing, max_accounts: parseInt(e.target.value)})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-500">Max Strategies</label>
                  <Input
                    type="number"
                    value={editedPricing.max_strategies ?? ''}
                    onChange={(e) => setEditedPricing({...editedPricing, max_strategies: parseInt(e.target.value)})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-500">Custom Strategy Limit</label>
                  <Input
                    type="number"
                    value={editedPricing.custom_strategy_limit ?? ''}
                    onChange={(e) => setEditedPricing({...editedPricing, custom_strategy_limit: parseInt(e.target.value)})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-500">Visual Strategy Limit</label>
                  <Input
                    type="number"
                    value={editedPricing.visual_strategy_limit ?? ''}
                    onChange={(e) => setEditedPricing({...editedPricing, visual_strategy_limit: parseInt(e.target.value)})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-zinc-500">Allowed Instruments (comma-separated)</label>
                <Input
                  value={(editedPricing.allowed_instruments || []).join(', ')}
                  onChange={(e) => setEditedPricing({
                    ...editedPricing,
                    allowed_instruments: e.target.value.split(',').map((entry) => entry.trim().toUpperCase()).filter(Boolean),
                  })}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPricingDialog(false)}>Cancel</Button>
              <Button onClick={handleSavePricing}>Save Pricing</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showMarketBrowserDialog} onOpenChange={setShowMarketBrowserDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl">
            <DialogHeader>
              <DialogTitle>Browse Historical Data Files</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {marketBrowserRoots.map((root) => (
                  <Button
                    key={root.path}
                    type="button"
                    variant={marketBrowserCurrentPath === root.path ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => loadMarketBrowser(root.path)}
                  >
                    <Folder className="w-4 h-4 mr-2" />
                    {root.label}
                  </Button>
                ))}
              </div>

              <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-3">
                <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">Current Folder</div>
                <div className="mt-1 break-all text-sm text-zinc-200">
                  {marketBrowserCurrentPath || 'No folder selected'}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!marketBrowserParentPath || marketBrowserLoading}
                    onClick={() => loadMarketBrowser(marketBrowserParentPath)}
                  >
                    Up
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!marketBrowserCurrentPath || marketBrowserLoading}
                    onClick={() => loadMarketBrowser(marketBrowserCurrentPath)}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${marketBrowserLoading ? 'animate-spin' : ''}`} />
                    Reload
                  </Button>
                </div>
              </div>

              <div className="max-h-[420px] overflow-auto rounded-lg border border-zinc-800 bg-zinc-950/60">
                {marketBrowserLoading ? (
                  <div className="px-4 py-10 text-sm text-zinc-500">Loading folders...</div>
                ) : marketBrowserEntries.length === 0 ? (
                  <div className="px-4 py-10 text-sm text-zinc-500">No folders or CSV files found in this location.</div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {marketBrowserEntries.map((entry) => (
                      <button
                        key={entry.path}
                        type="button"
                        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-zinc-900/80"
                        onClick={() => (
                          entry.type === 'directory'
                            ? loadMarketBrowser(entry.path)
                            : selectMarketDataFile(entry.path)
                        )}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          {entry.type === 'directory' ? (
                            <Folder className="h-4 w-4 shrink-0 text-blue-400" />
                          ) : (
                            <FileText className="h-4 w-4 shrink-0 text-green-400" />
                          )}
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">{entry.name}</div>
                            <div className="truncate text-xs text-zinc-500">{entry.path}</div>
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-xs text-zinc-500">
                          {entry.type === 'file' ? <div>{formatFileSize(entry.size_bytes)}</div> : <div>Folder</div>}
                          <div>{entry.modified_at ? formatDateTimeDisplay(entry.modified_at, appTimeZone) : ''}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="text-xs text-zinc-500">
                Only approved folders and `.csv` files are shown. The selected path must exist on the backend server.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMarketBrowserDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showStrategyDialog} onOpenChange={setShowStrategyDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl">
            <DialogHeader>
              <DialogTitle>Edit Strategy</DialogTitle>
            </DialogHeader>
            {editingStrategy && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    value={editingStrategy.name || ''}
                    onChange={(e) => setEditingStrategy({ ...editingStrategy, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="Strategy name"
                  />
                  <Select
                    value={editingStrategy.min_plan || 'FREE'}
                    onValueChange={(value) => setEditingStrategy({ ...editingStrategy, min_plan: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="FREE">FREE</SelectItem>
                      <SelectItem value="BASIC">BASIC</SelectItem>
                      <SelectItem value="PRO">PRO</SelectItem>
                      <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={editingStrategy.runtime_definition?.engine || 'TREND_FOLLOWING'}
                    onValueChange={updateEditingRuntimeEngine}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {STRATEGY_ENGINES.map((engine) => (
                        <SelectItem key={engine.value} value={engine.value}>
                          {engine.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <StrategyRuntimeFields
                  engine={editingStrategy.runtime_definition?.engine || 'TREND_FOLLOWING'}
                  params={editingStrategy.runtime_definition?.params || {}}
                  onChange={updateEditingRuntimeParam}
                  gridClassName="grid grid-cols-1 gap-3 md:grid-cols-2"
                  labelClassName="text-sm font-medium text-zinc-300"
                  inputClassName="bg-zinc-800 border-zinc-700 text-sm text-white"
                  switchCardClassName="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3"
                  hintClassName="text-xs text-zinc-500"
                />
                <Input
                  value={editingStrategy.description || ''}
                  onChange={(e) => setEditingStrategy({ ...editingStrategy, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Description"
                />
                <textarea
                  value={editingStrategy.code || ''}
                  onChange={(e) => setEditingStrategy({ ...editingStrategy, code: e.target.value })}
                  className="w-full min-h-[320px] rounded-md border border-zinc-700 bg-zinc-800 p-3 text-sm font-mono"
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStrategyDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveStrategy} disabled={savingStrategy}>
                {savingStrategy ? 'Saving...' : 'Save Strategy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PlatformShell>
    );
  }

  return (
    <PlatformShell title="Admin Dashboard" titleIcon={Shield} backTo="/dashboard" currentKey="admin" mainClassName="overflow-y-auto">
      <main className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Global Kill Switch Banner */}
        {systemSettings.kill_switch_enabled && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <Octagon className="w-8 h-8 text-red-500" />
              <div>
                <h3 className="font-bold text-red-400 text-lg">SYSTEM KILL SWITCH ACTIVE</h3>
                <p className="text-red-300 text-sm">All new orders are blocked. Open orders have been cancelled.</p>
              </div>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setShowKillSwitchConfirm(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              DEACTIVATE EMERGENCY STOP
            </Button>
          </div>
        )}

        <div className="flex justify-end mb-4">
          {!systemSettings.kill_switch_enabled && (
             <Button 
               variant="outline" 
               className="border-red-900/50 text-red-500 hover:bg-red-900/20 hover:text-red-400"
               onClick={() => setShowKillSwitchConfirm(true)}
             >
               <Octagon className="w-4 h-4 mr-2" />
               Emergency Stop
             </Button>
          )}
        </div>

        {adminLoadWarnings.length ? (
          <Card className="mb-6 border-amber-500/20 bg-amber-500/10">
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                <div>
                  <div className="font-medium text-amber-100">Admin data loaded with fallbacks</div>
                  <p className="text-sm text-amber-200/80">
                    Some sections could not be refreshed and are showing safe defaults:
                    {' '}
                    {adminLoadWarnings.join(', ')}.
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={fetchData} className="border-amber-400/30 text-amber-100 hover:bg-amber-500/10">
                Retry Load
              </Button>
            </CardContent>
          </Card>
        ) : null}

        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          <button
            type="button"
            onClick={() => navigate('/subscription')}
            className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 text-left transition hover:border-blue-500/40 hover:bg-zinc-900/90"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-500/10 p-2 text-blue-300">
                <CreditCard className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-white">Open Subscription</div>
                <div className="text-sm text-zinc-500">Review plans, access, and billing-facing flows.</div>
              </div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => navigate('/onboarding/broker-connect')}
            className="rounded-xl border border-zinc-800 bg-zinc-900 p-5 text-left transition hover:border-blue-500/40 hover:bg-zinc-900/90"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-500/10 p-2 text-blue-300">
                <Activity className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-white">Open Broker Connect</div>
                <div className="text-sm text-zinc-500">Jump straight to broker onboarding and connection management.</div>
              </div>
            </div>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
              <p className="text-xs text-green-400">+{stats?.new_users_this_month || 0} this month</p>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Active Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.active_subscriptions || 0}</div>
              <p className="text-xs text-zinc-500">Paid plans</p>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Monthly Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">
                ${stats?.revenue_monthly?.toFixed(2) || '0.00'}
              </div>
              <p className="text-xs text-zinc-500">Last 30 days</p>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">${stats?.revenue_total?.toFixed(2) || '0.00'}</div>
              <p className="text-xs text-zinc-500">All time</p>
            </CardContent>
          </Card>
        </div>

        {/* Plan Distribution */}
        <Card className="bg-zinc-900 border-zinc-800 mb-8">
          <CardHeader>
            <CardTitle className="text-sm text-zinc-400">Users by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              {stats?.users_by_plan && Object.entries(stats.users_by_plan).map(([plan, count]) => (
                <div key={plan} className="flex items-center gap-2">
                  <Badge variant={plan === 'FREE' ? 'secondary' : plan === 'PRO' ? 'default' : 'outline'}>
                    {plan}
                  </Badge>
                  <span className="text-lg font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-zinc-800/50">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="ai">AI</TabsTrigger>
            <TabsTrigger value="brokers">Brokers</TabsTrigger>
            <TabsTrigger value="operations">Operations</TabsTrigger>
            <TabsTrigger value="strategies">Strategies</TabsTrigger>
            <TabsTrigger value="support">Support Tickets</TabsTrigger>
            <TabsTrigger value="watchdog">Risk Monitor</TabsTrigger>
            <TabsTrigger value="automation_timing">Automation Timing</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersTab
              users={users}
              totalUsers={totalUsers}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              planFilter={planFilter}
              setPlanFilter={setPlanFilter}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              appTimeZone={appTimeZone}
              handleViewUser={handleViewUser}
              handleSendPasswordReset={handleSendPasswordReset}
              handleDeleteUser={handleDeleteUser}
            />
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <PricingTab
              pricing={pricing}
              pricingSettings={pricingSettings}
              savingPricingSettings={savingPricingSettings}
              togglePricingEnforcement={togglePricingEnforcement}
              handleTogglePricingEnforcement={handleTogglePricingEnforcement}
              handleEditPricing={handleEditPricing}
            />
          </TabsContent>

          <TabsContent value="ai">
            <AITab
              aiAnalysisSettings={aiAnalysisSettings}
              aiProviderPresetKey={aiProviderPresetKey}
              selectedAiProviderPreset={selectedAiProviderPreset}
              apiKeyEnvLooksLikeSecret={apiKeyEnvLooksLikeSecret}
              savingAiSettings={savingAiSettings}
              testingAiSettings={testingAiSettings}
              aiSettingsTestResult={aiSettingsTestResult}
              appTimeZone={appTimeZone}
              handleAiSettingsField={handleAiSettingsField}
              applyAiProviderPreset={applyAiProviderPreset}
              handleSaveAiSettings={handleSaveAiSettings}
              handleTestAiSettings={handleTestAiSettings}
              aiTradingAgentSettings={aiTradingAgentSettings}
              aiTradingAgentPresetKey={aiTradingAgentPresetKey}
              selectedAiTradingAgentPreset={selectedAiTradingAgentPreset}
              agentApiKeyEnvLooksLikeSecret={agentApiKeyEnvLooksLikeSecret}
              savingAiTradingAgentSettings={savingAiTradingAgentSettings}
              testingAiTradingAgentSettings={testingAiTradingAgentSettings}
              aiTradingAgentTestResult={aiTradingAgentTestResult}
              handleAiTradingAgentField={handleAiTradingAgentField}
            handleAiResearchContextField={handleAiResearchContextField}
            handleAiResearchTimeframeToggle={handleAiResearchTimeframeToggle}
              applyAiTradingAgentPreset={applyAiTradingAgentPreset}
              handleSaveAiTradingAgentSettings={handleSaveAiTradingAgentSettings}
              handleTestAiTradingAgentSettings={handleTestAiTradingAgentSettings}
              marketNewsEvents={marketNewsEvents}
              savingMarketNewsEvents={savingMarketNewsEvents}
              addMarketNewsEvent={addMarketNewsEvent}
              removeMarketNewsEvent={removeMarketNewsEvent}
              updateMarketNewsEventField={updateMarketNewsEventField}
              handleSaveMarketNewsEvents={handleSaveMarketNewsEvents}
            />
          </TabsContent>

          <TabsContent value="brokers">
            <div className="space-y-6">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Broker Instrument Registry</CardTitle>
                  <CardDescription>
                    Manage the instrument catalog each broker exposes to the app, including optional symbol and contract metadata.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {adminBrokers.length === 0 ? (
                    <div className="text-sm text-zinc-500">No broker configurations found.</div>
                  ) : (
                    <div className="space-y-4">
                      {adminBrokers.map((broker) => {
                        const draft = brokerInstrumentDrafts[broker.id] || [];
                        return (
                          <div key={broker.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div>
                                <div className="flex items-center gap-3">
                                  <div className="font-semibold text-white">{broker.display_name || broker.name}</div>
                                  <Badge variant={broker.is_active ? 'default' : 'secondary'}>
                                    {broker.is_active ? 'Active' : 'Disabled'}
                                  </Badge>
                                </div>
                                <div className="mt-1 text-sm text-zinc-500">{broker.description || 'No broker description'}</div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Switch
                                  checked={Boolean(broker.is_active)}
                                  disabled={savingBrokerId === broker.id}
                                  onCheckedChange={(checked) => handleToggleBrokerActive(broker, checked)}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveBroker(broker)}
                                  disabled={savingBrokerId === broker.id}
                                >
                                  {savingBrokerId === broker.id ? 'Saving...' : 'Save Broker'}
                                </Button>
                              </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                              {draft.map((entry) => (
                                <Badge key={`${broker.id}-${entry.symbol}`} variant="outline" className="flex items-center gap-2">
                                  <span>{entry.display_label || entry.symbol}</span>
                                  <button
                                    type="button"
                                    className="text-zinc-400 hover:text-white"
                                    onClick={() => removeBrokerInstrument(broker.id, entry.symbol)}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                              {!draft.length ? (
                                <span className="text-sm text-zinc-500">No instruments configured yet.</span>
                              ) : null}
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                              <div>
                                <label className="text-xs text-zinc-500">Open Time</label>
                                <Input
                                  value={broker.trading_hours?.start || ''}
                                  onChange={(e) => updateBrokerTradingHoursField(broker.id, 'start', e.target.value)}
                                  placeholder="18:00"
                                  className="bg-zinc-800 border-zinc-700"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500">Close Time</label>
                                <Input
                                  value={broker.trading_hours?.end || ''}
                                  onChange={(e) => updateBrokerTradingHoursField(broker.id, 'end', e.target.value)}
                                  placeholder="16:00"
                                  className="bg-zinc-800 border-zinc-700"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500">Timezone</label>
                                <Input
                                  value={broker.trading_hours?.timezone || ''}
                                  onChange={(e) => updateBrokerTradingHoursField(broker.id, 'timezone', e.target.value)}
                                  placeholder="America/New_York"
                                  className="bg-zinc-800 border-zinc-700"
                                />
                              </div>
                            </div>

                            {/* Day-of-week pickers — 0=Mon … 6=Sun */}
                            {(() => {
                              const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                              const toggleDay = (field, dayIndex) => {
                                const current = broker.trading_hours?.[field] ?? [];
                                const next = current.includes(dayIndex)
                                  ? current.filter((d) => d !== dayIndex)
                                  : [...current, dayIndex];
                                updateBrokerTradingHoursField(broker.id, field, next);
                              };
                              const DayRow = ({ field, label }) => (
                                <div>
                                  <label className="text-xs text-zinc-500">{label}</label>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {DAY_LABELS.map((name, idx) => {
                                      const active = (broker.trading_hours?.[field] ?? []).includes(idx);
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          onClick={() => toggleDay(field, idx)}
                                          className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                                            active
                                              ? 'bg-blue-600 text-white'
                                              : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                          }`}
                                        >
                                          {name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                              return (
                                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                  <DayRow field="open_days" label="Opens on (session starts)" />
                                  <DayRow field="close_days" label="Closes on (session ends)" />
                                </div>
                              );
                            })()}

                            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2">
                                <div className="pr-3">
                                  <div className="text-sm text-white">Enforce Trading Hours</div>
                                  <div className="text-xs text-zinc-500">Block new entries outside the broker session.</div>
                                </div>
                                <Switch
                                  checked={Boolean(broker.enforce_trading_hours)}
                                  disabled={savingBrokerId === broker.id}
                                  onCheckedChange={(checked) => updateBrokerConfigField(broker.id, 'enforce_trading_hours', checked)}
                                />
                              </div>
                              <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2">
                                <div className="pr-3">
                                  <div className="text-sm text-white">Force Flat At Close</div>
                                  <div className="text-xs text-zinc-500">Flatten positions during the session close window.</div>
                                </div>
                                <Switch
                                  checked={Boolean(broker.close_at_end_of_session)}
                                  disabled={savingBrokerId === broker.id}
                                  onCheckedChange={(checked) => updateBrokerConfigField(broker.id, 'close_at_end_of_session', checked)}
                                />
                              </div>
                            </div>

                            <div className="mt-4 flex flex-col gap-3 md:flex-row">
                              <Input
                                value={brokerInstrumentInputs[broker.id] || ''}
                                onChange={(e) => setBrokerInstrumentInputs((current) => ({ ...current, [broker.id]: e.target.value }))}
                                placeholder="Add symbol (ex: MCL, EURUSD, BTCUSD)"
                                className="bg-zinc-800 border-zinc-700"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => addBrokerInstrument(broker.id, brokerInstrumentInputs[broker.id])}
                              >
                                Add Instrument
                              </Button>
                            </div>

                            {draft.length ? (
                              <div className="mt-4 overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="border-zinc-800">
                                      <TableHead>Symbol</TableHead>
                                      <TableHead>Label</TableHead>
                                      <TableHead>Name</TableHead>
                                      <TableHead>Type</TableHead>
                                      <TableHead>Symbol ID</TableHead>
                                      <TableHead>Contract ID</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {draft.map((entry) => (
                                      <TableRow key={`${broker.id}-${entry.symbol}-row`} className="border-zinc-800">
                                        <TableCell className="font-medium text-white">{entry.symbol}</TableCell>
                                        <TableCell>
                                          <Input
                                            value={entry.display_label || ''}
                                            onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'display_label', e.target.value)}
                                            className="bg-zinc-800 border-zinc-700"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Input
                                            value={entry.display_name || ''}
                                            onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'display_name', e.target.value)}
                                            className="bg-zinc-800 border-zinc-700"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Select
                                            value={entry.instrument_type || 'futures'}
                                            onValueChange={(value) => updateBrokerInstrumentField(broker.id, entry.symbol, 'instrument_type', value)}
                                          >
                                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                              <SelectItem value="futures">futures</SelectItem>
                                              <SelectItem value="forex">forex</SelectItem>
                                              <SelectItem value="crypto">crypto</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </TableCell>
                                        <TableCell>
                                          <Input
                                            value={entry.symbol_id || ''}
                                            onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'symbol_id', e.target.value)}
                                            className="bg-zinc-800 border-zinc-700"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Input
                                            value={entry.contract_id || ''}
                                            onChange={(e) => updateBrokerInstrumentField(broker.id, entry.symbol, 'contract_id', e.target.value)}
                                            className="bg-zinc-800 border-zinc-700"
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Market Session Policies</CardTitle>
                  <CardDescription>
                    JSON source of truth for symbol aliases, broker overrides, and market-session templates.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <textarea
                    value={marketSessionConfigDraft}
                    onChange={(e) => setMarketSessionConfigDraft(e.target.value)}
                    className="min-h-[360px] w-full rounded-md border border-zinc-700 bg-zinc-950 p-3 font-mono text-sm text-white"
                    placeholder={`{\n  "policies": {}\n}`}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleSaveMarketSessionConfig} disabled={savingMarketSessionConfig}>
                      {savingMarketSessionConfig ? 'Saving...' : 'Save Session Config'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="operations">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr,1.05fr] gap-6">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Execution Queue Health</CardTitle>
                        <CardDescription>Platform-wide queue state and live broker-sync health.</CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Queued</div>
                      <div className="mt-1 text-2xl font-semibold">{executionOverview.jobs?.queued || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Retrying</div>
                      <div className="mt-1 text-2xl font-semibold text-yellow-400">{executionOverview.jobs?.retrying || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Dead Letters</div>
                      <div className="mt-1 text-2xl font-semibold text-red-400">{executionOverview.jobs?.dead_letter || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Live Trades</div>
                      <div className="mt-1 text-2xl font-semibold">{executionOverview.live_trades?.total || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Stale Sync</div>
                      <div className="mt-1 text-2xl font-semibold text-yellow-400">{executionOverview.live_trades?.stale_sync || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Anomalies</div>
                      <div className="mt-1 text-2xl font-semibold text-orange-400">{executionOverview.live_trades?.anomalies || 0}</div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Fill Reconciliation Stats</CardTitle>
                        <CardDescription>Historical performance of automated fill polling (last 7 days).</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-zinc-800 border-zinc-700">
                          {fillPollStats.total_cycles || 0} cycles
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Fills Detected</div>
                      <div className="mt-1 text-2xl font-semibold text-green-400">{fillPollStats.total_fills || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Ext. Closes</div>
                      <div className="mt-1 text-2xl font-semibold text-blue-400">{fillPollStats.total_external_closes || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Rejections</div>
                      <div className="mt-1 text-2xl font-semibold text-red-400">{fillPollStats.total_rejections || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-500">Avg. Duration</div>
                      <div className="mt-1 text-2xl font-semibold">
                        {fillPollHistory.length > 0 
                          ? Math.round(fillPollHistory.reduce((acc, log) => acc + (log.duration_ms || 0), 0) / fillPollHistory.length)
                          : 0}ms
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {renderDeadLetterCleanupCard()}

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>ProjectX Broker Gap Repair</CardTitle>
                  <CardDescription>
                    Preview an exact one-minute historical window, then queue a governed broker backfill and aggregate rebuild.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <label className="text-xs text-zinc-500">Symbol</label>
                      <Select
                        value={brokerGapRepairForm.symbol}
                        onValueChange={(value) => handleBrokerGapRepairField('symbol', value)}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {marketDataSymbolOptions.map((option) => (
                            <SelectItem key={`desktop-gap-${option.value}`} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500">Start (browser local time)</label>
                      <Input
                        ref={brokerGapRepairStartInputRef}
                        type="datetime-local"
                        value={brokerGapRepairForm.start_time}
                        onChange={(event) => handleBrokerGapRepairField('start_time', event.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500">End (browser local time)</label>
                      <Input
                        ref={brokerGapRepairEndInputRef}
                        type="datetime-local"
                        value={brokerGapRepairForm.end_time}
                        onChange={(event) => handleBrokerGapRepairField('end_time', event.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500">Maximum one-minute bars</label>
                      <Input
                        type="number"
                        min="60"
                        max="30000"
                        step="1"
                        value={brokerGapRepairForm.maximum_bars}
                        onChange={(event) => handleBrokerGapRepairField('maximum_bars', event.target.value)}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 rounded-md border border-cyan-500/20 bg-cyan-500/10 p-3 text-xs text-cyan-100">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      Preview is database-only. Apply contacts ProjectX for historical candles, never places orders, and leaves scheduled maintenance periods empty.
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handlePreviewBrokerGapRepair}
                    disabled={previewingBrokerGapRepair || applyingBrokerGapRepair}
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${previewingBrokerGapRepair ? 'animate-spin' : ''}`} />
                    {previewingBrokerGapRepair ? 'Checking coverage...' : 'Preview Broker Repair'}
                  </Button>

                  {brokerGapRepairPreview ? (
                    <div className="space-y-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <div><div className="text-xs text-zinc-500">Requested</div><div className="text-xl font-semibold">{brokerGapRepairPreview.requested_intervals}</div></div>
                        <div><div className="text-xs text-zinc-500">Open-session</div><div className="text-xl font-semibold">{brokerGapRepairPreview.scheduled_open_intervals}</div></div>
                        <div><div className="text-xs text-zinc-500">Closed-session</div><div className="text-xl font-semibold text-zinc-400">{brokerGapRepairPreview.scheduled_closed_intervals}</div></div>
                        <div><div className="text-xs text-zinc-500">Existing</div><div className="text-xl font-semibold text-emerald-300">{brokerGapRepairPreview.existing_intervals_any_provider}</div></div>
                        <div><div className="text-xs text-zinc-500">ProjectX existing</div><div className="text-xl font-semibold text-cyan-300">{brokerGapRepairPreview.existing_intervals_topstepx}</div></div>
                        <div><div className="text-xs text-zinc-500">Missing</div><div className="text-xl font-semibold text-amber-300">{brokerGapRepairPreview.missing_scheduled_intervals}</div></div>
                      </div>
                      <div className="text-xs text-zinc-400">
                        UTC window: {brokerGapRepairPreview.start_time} to {brokerGapRepairPreview.end_time}
                      </div>
                      {brokerGapRepairPreview.will_contact_broker ? (
                        <div className="flex flex-col gap-2 md:flex-row">
                          <Input
                            value={brokerGapRepairConfirmation}
                            onChange={(event) => setBrokerGapRepairConfirmation(event.target.value)}
                            placeholder={brokerGapRepairPreview.confirmation_phrase}
                            className="bg-zinc-800 border-zinc-700 font-mono"
                          />
                          <Button
                            onClick={handleApplyBrokerGapRepair}
                            disabled={
                              applyingBrokerGapRepair
                              || brokerGapRepairConfirmation.trim() !== brokerGapRepairPreview.confirmation_phrase
                            }
                          >
                            <RefreshCw className={`mr-2 h-4 w-4 ${applyingBrokerGapRepair ? 'animate-spin' : ''}`} />
                            {applyingBrokerGapRepair ? 'Queueing...' : 'Apply Broker Repair'}
                          </Button>
                        </div>
                      ) : (
                        <div className="text-sm text-emerald-300">No scheduled one-minute bars are missing in this range.</div>
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr,1.05fr] gap-6">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle>Recent Fill Polling Cycles</CardTitle>
                    <CardDescription>Latest reconciliation runs and their outcomes.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {fillPollHistory.length === 0 ? (
                      <div className="text-sm text-zinc-500">No polling logs yet.</div>
                    ) : (
                      <div className="space-y-2 overflow-auto max-h-[300px] pr-2">
                        {fillPollHistory.map((log) => (
                          <div key={log.id} className="text-xs p-2 rounded border border-zinc-800 bg-zinc-950/40 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <span className="text-zinc-500 font-mono">
                                {formatDateTimeDisplay(log.created_at, appTimeZone)}
                              </span>
                              <span className="text-zinc-300">Checked {log.checked}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {log.filled > 0 && <Badge variant="default" className="bg-green-500/10 text-green-400 border-green-500/20">{log.filled} filled</Badge>}
                              {log.closed_externally > 0 && <Badge variant="outline" className="border-blue-500/20 text-blue-400">{log.closed_externally} ext</Badge>}
                              {log.rejected > 0 && <Badge variant="destructive">{log.rejected} rej</Badge>}
                              {log.errors > 0 && <Badge variant="destructive" className="bg-red-500">{log.errors} err</Badge>}
                              <span className="text-zinc-600 ml-2">{log.duration_ms}ms</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle>Recent Dead Letters</CardTitle>
                    <CardDescription>Jobs that exhausted retries and need review.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {executionOverview.dead_letters?.length === 0 ? (
                      <div className="text-sm text-zinc-500">No dead letters right now.</div>
                    ) : (
                      <div className="space-y-3">
                        {executionOverview.dead_letters.map((job) => (
                          <div key={job.id} className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-medium text-white">
                                  {job.job_type} {job.broker_name ? `• ${job.broker_name}` : ""}
                                </div>
                                <div className="text-xs text-zinc-500">
                                  User {job.user_id?.slice(0, 8)} • Trade {job.trade_id?.slice(0, 8) || "n/a"}
                                </div>
                                <div className="mt-1 text-xs text-red-300">{job.last_error || "No error detail"}</div>
                              </div>
                              <Badge variant="destructive">
                                {job.dead_lettered_at ? formatDateTimeDisplay(job.dead_lettered_at, appTimeZone) : "Dead letter"}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Recent Execution Jobs</CardTitle>
                  <CardDescription>Latest broker execution activity across all users, with retry controls for failed jobs.</CardDescription>
                </CardHeader>
                <CardContent>
                  {executionJobs.length === 0 ? (
                    <div className="text-sm text-zinc-500">No execution jobs yet.</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead>Job</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Attempts</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Trade</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead className="text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {executionJobs.map((job) => {
                          const canRetry = job.status === 'failed' || job.status === 'dead_letter';
                          return (
                            <TableRow key={job.id} className="border-zinc-800">
                              <TableCell>
                                <div>
                                  <div className="font-medium text-white">
                                    {job.job_type} {job.broker_name ? `• ${job.broker_name}` : ''}
                                  </div>
                                  <div className="text-xs text-zinc-500">{job.id.slice(0, 8)}</div>
                                  {job.last_error ? (
                                    <div className="mt-1 text-xs text-red-300">{job.last_error}</div>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={job.status === 'dead_letter' || job.status === 'failed' ? 'destructive' : 'outline'}
                                  className={job.status === 'retrying' ? 'border-yellow-500/40 text-yellow-300' : ''}
                                >
                                  {job.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-zinc-300">
                                {job.attempts}/{job.max_attempts}
                              </TableCell>
                              <TableCell className="text-zinc-500">{job.user_id?.slice(0, 8) || 'n/a'}</TableCell>
                              <TableCell className="text-zinc-500">{job.trade_id?.slice(0, 8) || 'n/a'}</TableCell>
                              <TableCell className="text-zinc-500 text-sm">
                                {job.created_at ? formatDateTimeDisplay(job.created_at, appTimeZone) : 'n/a'}
                              </TableCell>
                              <TableCell className="text-right">
                                {canRetry ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={retryingExecutionJobId === job.id}
                                    onClick={() => handleRetryExecutionJob(job.id)}
                                  >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${retryingExecutionJobId === job.id ? 'animate-spin' : ''}`} />
                                    Retry
                                  </Button>
                                ) : (
                                  <span className="text-xs text-zinc-600">No action</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-6">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Historical Data Refresh</CardTitle>
                        <CardDescription>
                          Queue Dukascopy CSV refresh jobs (UTC), then auto-rebuild chart aggregates.
                        </CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchData}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reload
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-zinc-500">Provider</label>
                        <Select
                          value={marketRefreshForm.provider}
                          onValueChange={(value) => setMarketRefreshForm((current) => ({ ...current, provider: value }))}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="dukascopy">dukascopy</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500">Mode</label>
                        <Select
                          value={marketRefreshForm.mode}
                          onValueChange={(value) => setMarketRefreshForm((current) => ({ ...current, mode: value }))}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="incremental">incremental</SelectItem>
                            <SelectItem value="full">full</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-zinc-500">Symbol</label>
                        <Select
                          value={marketRefreshForm.symbol}
                          onValueChange={handleMarketRefreshSymbolChange}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {marketDataSymbolOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500">Timezone</label>
                        <Input
                          value={marketRefreshForm.source_timezone}
                          onChange={(e) => setMarketRefreshForm((current) => ({ ...current, source_timezone: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="UTC"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500">CSV Path (server)</label>
                      <div className="flex gap-2">
                        <Input
                          value={marketRefreshForm.csv_path}
                          onChange={(e) => setMarketRefreshForm((current) => ({ ...current, csv_path: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="/data/raw/dukascopy/BTCUSD/file.csv"
                        />
                        <Button type="button" variant="outline" onClick={openMarketBrowser}>
                          <FolderOpen className="w-4 h-4 mr-2" />
                          Browse
                        </Button>
                      </div>
                      <div className="mt-2 text-xs text-zinc-500">
                        Valid CSV filenames must contain `{marketRefreshForm.symbol}` somewhere in the filename.
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-zinc-500">Session Type</label>
                        <Input
                          value={marketRefreshForm.session_type}
                          onChange={(e) => setMarketRefreshForm((current) => ({ ...current, session_type: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="no_session"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500">Aggregate Timeframes (comma-separated)</label>
                        <Input
                          value={marketRefreshForm.timeframes}
                          onChange={(e) => setMarketRefreshForm((current) => ({ ...current, timeframes: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                          placeholder="2m,3m,5m,15m,30m,1h,4h,1d,1w,1mo"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={handleRunMarketRefresh}
                      disabled={runningMarketRefresh}
                      className="w-full"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${runningMarketRefresh ? 'animate-spin' : ''}`} />
                      {runningMarketRefresh ? 'Queueing refresh...' : 'Queue Historical Data Refresh'}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle>Market Data Coverage</CardTitle>
                    <CardDescription>UTC coverage currently available to chart APIs.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {marketDataCoverage.length === 0 ? (
                      <div className="text-sm text-zinc-500">No historical data loaded yet.</div>
                    ) : (
                      <div className="space-y-3 max-h-[360px] overflow-auto pr-1">
                        {marketDataCoverage.map((entry) => (
                          <div key={`${entry.provider}-${entry.symbol}`} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-white">{entry.provider} • {entry.symbol}</div>
                              <Badge variant="outline">{entry.bar_count_1m || 0} bars (1m)</Badge>
                            </div>
                            <div className="mt-2 text-xs text-zinc-500">
                              {entry.min_ts_utc || 'n/a'} → {entry.max_ts_utc || 'n/a'}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {Object.entries(entry.aggregates || {}).map(([tf, count]) => (
                                <Badge key={`${entry.symbol}-${tf}`} variant="secondary" className="text-xs">
                                  {tf}: {count}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Refresh Jobs</CardTitle>
                  <CardDescription>Latest historical refresh jobs submitted by admin.</CardDescription>
                </CardHeader>
                <CardContent>
                  {marketDataJobs.length === 0 ? (
                    <div className="text-sm text-zinc-500">No refresh jobs yet.</div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800">
                          <TableHead>Job</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Rows</TableHead>
                          <TableHead>Loaded</TableHead>
                          <TableHead>Skipped</TableHead>
                          <TableHead>Rejected</TableHead>
                          <TableHead>Window</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {marketDataJobs.map((job) => (
                          <TableRow key={job.id} className="border-zinc-800">
                            <TableCell>
                              <div className="font-medium text-white">
                                {job.provider} • {job.symbol}
                              </div>
                              <div className="text-xs text-zinc-500">{job.mode} • {job.id.slice(0, 8)}</div>
                              <div className="text-xs text-zinc-600 break-all">{job.csv_path}</div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={job.status === 'failed' ? 'destructive' : 'outline'}
                                className={job.status === 'running' ? 'border-blue-500/40 text-blue-300' : ''}
                              >
                                {job.status}
                              </Badge>
                              {job.error_message ? (
                                <div className="mt-1 text-xs text-red-300">{job.error_message}</div>
                              ) : null}
                            </TableCell>
                            <TableCell className="text-zinc-300">{job.rows_total || 0}</TableCell>
                            <TableCell className="text-green-400">{job.rows_loaded || 0}</TableCell>
                            <TableCell className="text-yellow-400">{job.rows_skipped || 0}</TableCell>
                            <TableCell className="text-red-300">{job.rows_rejected || 0}</TableCell>
                            <TableCell className="text-xs text-zinc-500">
                              <div>{job.source_start_ts_utc || 'n/a'}</div>
                              <div>{job.source_end_ts_utc || 'n/a'}</div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Strategies Tab */}
          <TabsContent value="strategies">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle>Upload Python Strategy</CardTitle>
                  <CardDescription>Add strategy code to platform catalog</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Strategy name"
                    value={newStrategy.name}
                    onChange={(e) => setNewStrategy({ ...newStrategy, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <Input
                    placeholder="Short description"
                    value={newStrategy.description}
                    onChange={(e) => setNewStrategy({ ...newStrategy, description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  <Select
                    value={newStrategy.min_plan}
                    onValueChange={(value) => setNewStrategy({ ...newStrategy, min_plan: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Minimum plan" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="FREE">FREE</SelectItem>
                      <SelectItem value="BASIC">BASIC</SelectItem>
                      <SelectItem value="PRO">PRO</SelectItem>
                      <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={newStrategy.runtime_engine}
                    onValueChange={updateCatalogRuntimeEngine}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Execution engine" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {STRATEGY_ENGINES.map((engine) => (
                        <SelectItem key={engine.value} value={engine.value}>
                          {engine.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <StrategyRuntimeFields
                    engine={newStrategy.runtime_engine}
                    params={newStrategy.runtime_params}
                    onChange={updateCatalogRuntimeParam}
                    gridClassName="grid grid-cols-1 gap-3 md:grid-cols-2"
                    labelClassName="text-sm font-medium text-zinc-300"
                    inputClassName="bg-zinc-800 border-zinc-700 text-sm text-white"
                    switchCardClassName="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3"
                    hintClassName="text-xs text-zinc-500"
                  />
                  <textarea
                    placeholder="Paste Python strategy code here..."
                    value={newStrategy.code}
                    onChange={(e) => setNewStrategy({ ...newStrategy, code: e.target.value })}
                    className="w-full min-h-[220px] p-3 rounded-md bg-zinc-800 border border-zinc-700 text-sm font-mono"
                  />
                  <Button onClick={handleCreateCatalogStrategy} className="w-full">
                    Upload Strategy
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Existing Catalog Strategies</CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchCatalogStrategies}>
                      <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {catalogStrategies.length === 0 ? (
                    <div className="text-zinc-500 text-sm">No catalog strategies yet.</div>
                  ) : (
                    <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
                      {catalogStrategies.map((s) => (
                        <div key={s.id} className="border border-zinc-800 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="font-semibold">{s.name}</div>
                              <div className="text-xs text-zinc-500">{s.description || 'No description'}</div>
                              <div className="mt-2 flex gap-2">
                                <Badge variant="outline">{s.min_plan}</Badge>
                                <Badge variant="outline">
                                  {STRATEGY_ENGINES.find((engine) => engine.value === s.runtime_definition?.engine)?.label || 'Trend Following'}
                                </Badge>
                                <Badge variant={s.is_active ? 'default' : 'secondary'}>
                                  {s.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditStrategy(s)}>
                                Edit
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => requestToggleCatalogStrategy(s)}>
                                {s.is_active ? 'Disable' : 'Enable'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => requestDeleteCatalogStrategy(s.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Support Tickets
                </CardTitle>
                <CardDescription>Escalated conversations requiring human response</CardDescription>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No support tickets yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead>Subject</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tickets.map((ticket) => (
                        <TableRow key={ticket.id} className="border-zinc-800">
                          <TableCell className="font-medium">{ticket.subject}</TableCell>
                          <TableCell className="text-zinc-500">{ticket.user_email}</TableCell>
                          <TableCell>
                            <Badge variant={
                              ticket.priority === 'high' ? 'destructive' :
                              ticket.priority === 'medium' ? 'default' : 'secondary'
                            }>
                              {ticket.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              ticket.status === 'open' ? 'outline' :
                              ticket.status === 'resolved' ? 'default' : 'secondary'
                            }>
                              {ticket.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {formatDateTimeDisplay(ticket.created_at, appTimeZone)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Select
                              value={ticket.status}
                              onValueChange={(value) => handleUpdateTicket(ticket.id, value)}
                            >
                              <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800">
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="watchdog">
            <WatchdogTab />
          </TabsContent>

          <TabsContent value="automation_timing">
            <AutomationTimingTab />
          </TabsContent>

          <TabsContent value="system">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Platform System Settings
                </CardTitle>
                <CardDescription>Global configuration for platform behavior and performance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Dashboard Data Refresh Rate (ms)</label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        value={systemSettings.data_refresh_rate_ms}
                        onChange={(e) => setSystemSettings({ ...systemSettings, data_refresh_rate_ms: parseInt(e.target.value, 10) })}
                        className="bg-zinc-800 border-zinc-700"
                      />
                      <Button 
                        onClick={handleUpdateSystemSettings} 
                        disabled={savingSystemSettings}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {savingSystemSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Controls how often the dashboard UI polls for price updates and account state.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Payment Enforcement</label>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium">Require Active Subscription</div>
                        <div className="text-xs text-zinc-500">Gate features based on plan limits</div>
                      </div>
                      <Switch
                        checked={systemSettings.payments_enforced}
                        onCheckedChange={(checked) => {
                          const next = { ...systemSettings, payments_enforced: checked };
                          setSystemSettings(next);
                          setPricingSettings({ payments_enforced: checked });
                          updateSystemSettings(next).then(() => {
                            toast.success('Payment enforcement updated');
                            fetchData();
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Live Order Quote Guardrail</label>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                      <div className="space-y-0.5">
                        <div className="text-sm font-medium">Require Fresh Broker Quote</div>
                        <div className="text-xs text-zinc-500">Block live orders when the latest quote is missing or stale.</div>
                      </div>
                      <Switch
                        checked={Boolean(systemSettings.live_order_require_fresh_quote)}
                        onCheckedChange={(checked) => setSystemSettings({
                          ...systemSettings,
                          live_order_require_fresh_quote: checked,
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Live Order Quote Max Age (seconds)</label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        min="2"
                        max="300"
                        step="1"
                        value={systemSettings.live_order_quote_max_age_seconds}
                        onChange={(e) => setSystemSettings({
                          ...systemSettings,
                          live_order_quote_max_age_seconds: Number.parseFloat(e.target.value) || 2,
                        })}
                        className="bg-zinc-800 border-zinc-700"
                      />
                      <Button
                        onClick={handleUpdateSystemSettings}
                        disabled={savingSystemSettings}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {savingSystemSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Admin-controlled freshness threshold used before a live order can be queued. Default is 12 seconds.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-400">Signal Recipient Activity Window (days)</label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        min="1"
                        max="3650"
                        step="1"
                        value={systemSettings.signal_recipient_active_days}
                        onChange={(e) => setSystemSettings({
                          ...systemSettings,
                          signal_recipient_active_days: Number.parseInt(e.target.value, 10) || 1,
                        })}
                        className="bg-zinc-800 border-zinc-700"
                      />
                      <Button
                        onClick={handleUpdateSystemSettings}
                        disabled={savingSystemSettings}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {savingSystemSettings ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Send strategy signal push and email alerts only to users who signed in within this window.
                    </p>
                  </div>
                </div>

                {renderNotificationSummarySchedule()}

                <div className="space-y-4 pt-4 border-t border-zinc-800">
                  <h3 className="text-md font-medium text-zinc-300">Integrations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Resend Status Card */}
                    <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Mail className="w-5 h-5 text-zinc-400" />
                          <div>
                            <div className="font-medium">Email Service (Resend)</div>
                            <div className="text-xs text-zinc-500">Transactional emails & alerts</div>
                          </div>
                        </div>
                        <Badge variant={resendStatus?.status === 'connected' ? 'default' : 'destructive'}>
                          {resendStatus?.status === 'connected' ? 'Connected' : 'Error'}
                        </Badge>
                      </div>
                      
                      {resendStatus?.status === 'connected' ? (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Sender:</span>
                            <span className="font-mono text-zinc-300">{resendStatus.sender_email}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Verified Domains:</span>
                            <span className="text-zinc-300">{resendStatus.active_domains} / {resendStatus.verified_domains}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-red-400">
                          {resendStatus?.message || 'Configuration missing or API key invalid'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-zinc-800">
                  <h3 className="text-md font-medium text-red-400 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Danger Zone
                  </h3>
                  <div className="p-4 rounded-lg border border-red-900/30 bg-red-950/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold text-red-500">Global Kill Switch</div>
                        <div className="text-sm text-zinc-400 max-w-md mt-1">
                          Immediately blocks all new orders and attempts to cancel all open orders across the entire platform.
                          Use only in emergencies.
                        </div>
                      </div>
                      <Button 
                        variant={systemSettings.kill_switch_enabled ? "destructive" : "outline"}
                        className={systemSettings.kill_switch_enabled 
                          ? "bg-red-600 hover:bg-red-700 border-red-500" 
                          : "border-red-900/50 text-red-500 hover:bg-red-900/20"
                        }
                        onClick={() => setShowKillSwitchConfirm(true)}
                      >
                        {systemSettings.kill_switch_enabled ? "DEACTIVATE KILL SWITCH" : "ACTIVATE KILL SWITCH"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Kill Switch Confirmation Dialog */}
      <Dialog open={showKillSwitchConfirm} onOpenChange={setShowKillSwitchConfirm}>
        <DialogContent className="bg-zinc-900 border-red-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2 text-xl">
              <Octagon className="w-6 h-6" />
              {systemSettings.kill_switch_enabled ? "Deactivate Kill Switch?" : "ACTIVATE GLOBAL KILL SWITCH?"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {systemSettings.kill_switch_enabled ? (
              <p className="text-zinc-300">
                This will resume normal trading operations. Users will be able to place orders again immediately.
              </p>
            ) : (
              <div className="space-y-3">
                <p className="font-bold text-white text-lg">⚠️ EXTREME CAUTION REQUIRED</p>
                <ul className="list-disc list-inside space-y-2 text-zinc-300">
                  <li>All <strong>new order submissions</strong> will be blocked immediately.</li>
                  <li>The system will attempt to <strong>cancel ALL open orders</strong> on all broker connections.</li>
                  <li>This action affects <strong>ALL USERS</strong> on the platform.</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowKillSwitchConfirm(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleToggleKillSwitch}
              disabled={killSwitchProcessing}
              className="bg-red-600 hover:bg-red-700"
            >
              {killSwitchProcessing 
                ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> 
                : (systemSettings.kill_switch_enabled ? "Resume Trading" : "ACTIVATE KILL SWITCH")
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-500">Email</label>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="text-sm text-zinc-500">Name</label>
                  <p className="font-medium">{selectedUser.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-zinc-500">Role</label>
                  <Select
                    value={selectedUser.role}
                    onValueChange={(value) => setSelectedUser({...selectedUser, role: value})}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-zinc-500">Subscription Plan</label>
                  <Select
                    value={selectedUser.subscription?.plan}
                    onValueChange={(value) => setSelectedUser({
                      ...selectedUser, 
                      subscription: {...selectedUser.subscription, plan: value}
                    })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="FREE">FREE</SelectItem>
                      <SelectItem value="BASIC">BASIC</SelectItem>
                      <SelectItem value="PRO">PRO</SelectItem>
                      <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-zinc-500">Broker Connections</label>
                <p className="font-medium">{selectedUser.broker_connections?.length || 0} connection(s)</p>
              </div>
              
              <div>
                <label className="text-sm text-zinc-500">Strategies</label>
                <p className="font-medium">{selectedUser.strategies?.length || 0} strategy(s)</p>
              </div>
              
              {selectedUser.payment_history?.length > 0 && (
                <div>
                  <label className="text-sm text-zinc-500">Recent Payments</label>
                  <div className="mt-2 space-y-2">
                    {selectedUser.payment_history.slice(0, 3).map((payment, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{payment.plan}</span>
                        <span className="text-green-400">${payment.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button onClick={() => handleUpdateUser(selectedUser.id, {
              role: selectedUser.role,
              subscription_plan: selectedUser.subscription?.plan
            })}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Edit Dialog */}
      <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Edit {selectedPlan} Pricing</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-500">Monthly Price ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editedPricing.price_monthly || ''}
                  onChange={(e) => setEditedPricing({...editedPricing, price_monthly: parseFloat(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Yearly Price ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editedPricing.price_yearly || ''}
                  onChange={(e) => setEditedPricing({...editedPricing, price_yearly: parseFloat(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Max Accounts (-1 = unlimited)</label>
                <Input
                  type="number"
                  value={editedPricing.max_accounts || ''}
                  onChange={(e) => setEditedPricing({...editedPricing, max_accounts: parseInt(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Max Strategies (-1 = unlimited)</label>
                <Input
                  type="number"
                  value={editedPricing.max_strategies || ''}
                  onChange={(e) => setEditedPricing({...editedPricing, max_strategies: parseInt(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Custom Strategy Limit</label>
                <Input
                  type="number"
                  value={editedPricing.custom_strategy_limit ?? ''}
                  onChange={(e) => setEditedPricing({...editedPricing, custom_strategy_limit: parseInt(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-500">Visual Strategy Limit</label>
                <Input
                  type="number"
                  value={editedPricing.visual_strategy_limit ?? ''}
                  onChange={(e) => setEditedPricing({...editedPricing, visual_strategy_limit: parseInt(e.target.value)})}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-zinc-500">Allowed Instruments (comma-separated)</label>
              <Input
                value={(editedPricing.allowed_instruments || []).join(', ')}
                onChange={(e) => setEditedPricing({
                  ...editedPricing,
                  allowed_instruments: e.target.value.split(',').map((entry) => entry.trim().toUpperCase()).filter(Boolean),
                })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPricingDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePricing}>Save Pricing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMarketBrowserDialog} onOpenChange={setShowMarketBrowserDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl">
          <DialogHeader>
            <DialogTitle>Browse Historical Data Files</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {marketBrowserRoots.map((root) => (
                <Button
                  key={root.path}
                  type="button"
                  variant={marketBrowserCurrentPath === root.path ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => loadMarketBrowser(root.path)}
                >
                  <Folder className="w-4 h-4 mr-2" />
                  {root.label}
                </Button>
              ))}
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-3">
              <div className="text-xs uppercase tracking-[0.22em] text-zinc-500">Current Folder</div>
              <div className="mt-1 break-all text-sm text-zinc-200">
                {marketBrowserCurrentPath || 'No folder selected'}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!marketBrowserParentPath || marketBrowserLoading}
                  onClick={() => loadMarketBrowser(marketBrowserParentPath)}
                >
                  Up
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!marketBrowserCurrentPath || marketBrowserLoading}
                  onClick={() => loadMarketBrowser(marketBrowserCurrentPath)}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${marketBrowserLoading ? 'animate-spin' : ''}`} />
                  Reload
                </Button>
              </div>
            </div>

            <div className="max-h-[420px] overflow-auto rounded-lg border border-zinc-800 bg-zinc-950/60">
              {marketBrowserLoading ? (
                <div className="px-4 py-10 text-sm text-zinc-500">Loading folders...</div>
              ) : marketBrowserEntries.length === 0 ? (
                <div className="px-4 py-10 text-sm text-zinc-500">No folders or CSV files found in this location.</div>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {marketBrowserEntries.map((entry) => (
                    <button
                      key={entry.path}
                      type="button"
                      className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-zinc-900/80"
                      onClick={() => (
                        entry.type === 'directory'
                          ? loadMarketBrowser(entry.path)
                          : selectMarketDataFile(entry.path)
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        {entry.type === 'directory' ? (
                          <Folder className="h-4 w-4 shrink-0 text-blue-400" />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0 text-green-400" />
                        )}
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-white">{entry.name}</div>
                          <div className="truncate text-xs text-zinc-500">{entry.path}</div>
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs text-zinc-500">
                        {entry.type === 'file' ? <div>{formatFileSize(entry.size_bytes)}</div> : <div>Folder</div>}
                        <div>{entry.modified_at ? formatDateTimeDisplay(entry.modified_at, appTimeZone) : ''}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="text-xs text-zinc-500">
              Only approved folders and `.csv` files are shown. The selected path must exist on the backend server.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarketBrowserDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showStrategyDialog} onOpenChange={setShowStrategyDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl flex flex-col max-h-[90vh]">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Edit Strategy</DialogTitle>
          </DialogHeader>
          {editingStrategy && (
            <div className="overflow-y-auto flex-1 space-y-4 pr-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  value={editingStrategy.name || ''}
                  onChange={(e) => setEditingStrategy({ ...editingStrategy, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Strategy name"
                />
                <Select
                  value={editingStrategy.min_plan || 'FREE'}
                  onValueChange={(value) => setEditingStrategy({ ...editingStrategy, min_plan: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="FREE">FREE</SelectItem>
                    <SelectItem value="BASIC">BASIC</SelectItem>
                    <SelectItem value="PRO">PRO</SelectItem>
                    <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={editingStrategy.runtime_definition?.engine || 'TREND_FOLLOWING'}
                  onValueChange={updateEditingRuntimeEngine}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {STRATEGY_ENGINES.map((engine) => (
                      <SelectItem key={engine.value} value={engine.value}>
                        {engine.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <StrategyRuntimeFields
                engine={editingStrategy.runtime_definition?.engine || 'TREND_FOLLOWING'}
                params={editingStrategy.runtime_definition?.params || {}}
                onChange={updateEditingRuntimeParam}
                gridClassName="grid grid-cols-1 gap-3 md:grid-cols-2"
                labelClassName="text-sm font-medium text-zinc-300"
                inputClassName="bg-zinc-800 border-zinc-700 text-sm text-white"
                switchCardClassName="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3"
                hintClassName="text-xs text-zinc-500"
              />
              <Input
                value={editingStrategy.description || ''}
                onChange={(e) => setEditingStrategy({ ...editingStrategy, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Description"
              />
              <textarea
                value={editingStrategy.code || ''}
                onChange={(e) => setEditingStrategy({ ...editingStrategy, code: e.target.value })}
                className="w-full min-h-[320px] p-3 rounded-md bg-zinc-800 border border-zinc-700 text-sm font-mono"
              />
            </div>
          )}
          <DialogFooter className="flex-shrink-0 pt-2">
            <Button variant="outline" onClick={() => setShowStrategyDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveStrategy} disabled={savingStrategy}>
              {savingStrategy ? 'Saving...' : 'Save Strategy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PlatformShell>
  );
};

export default AdminDashboard;
